import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  ASSET_FAMILY_FACTS,
  ChatMessageCollectAttachItem,
  MaterialAttachOffer,
  MaterialOffers,
  QuestionnaireChatMessage,
  QuestionnaireCollectedAssetOperation,
  QuestionnaireCollectedItem,
  QuestionnaireDraft,
  QuestionnaireDraftReadiness,
  QuestionnaireOperationEffect,
  QuestionnaireTurnCommand,
  StudyDesign,
  SurveyJson,
  TESTED_ASSET_MAX_BY_TYPE,
  TestedAssetBlock,
  TestedAssetType,
  FILES_PER_ASSET,
  LINK_MATERIAL_FAMILIES,
  goalRecipe,
  isAssetFamily,
  linksOnly,
  materialOffers,
  messageLinks,
  nonKeyframeableVideoMessage,
  openClarificationOf,
  resolveStudyBrand,
  respondentWordingFor,
  studyMaterialFamilies,
  studySpecOf,
} from "@digital-twins/shared-types";
import { ArrowLeft, ArrowRight, Loader2, Paperclip, Square } from "lucide-react";
import { toast } from "sonner";

import type { IssuedAssetFile } from "@/lib/api/client";

import type { StudyType } from "@/features/studies/creation/types";
import { useAutosizeTextarea } from "@/lib/hooks/use-autosize-textarea";
import { getUserInitials } from "@/lib/utils/avatar";
import { CHAT_TEXTAREA_MAX_HEIGHT_PX } from "@/lib/utils/sync-textarea-autosize-height";
import { cn } from "@/lib/utils";
import { languageName } from "@/features/studies/questionnaire/utils/turnCommandCopy";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Text } from "@/components/ui/Text";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/ui/UserAvatar";
import {
  AttachmentTiles,
  type AttachmentTileItem,
} from "@/components/chat/AttachmentTiles";
import { useAuth } from "@/lib/config/auth-provider";
import {
  captureQuestionnaireUrlApi,
  uploadQuestionnaireAssetFileApi,
} from "../api/questionnaireDraftsApi";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { renderPdfFirstPagePreview } from "@/lib/utils/extract-pdf-text";
import { friendlyCaptureError } from "@/components/media/useUrlCaptures";

import {
  AssistantReplyMessage,
  assistantReplyLines,
  ReplyLines,
} from "./AssistantReplyLines";
import { CollectCard } from "./CollectCard";
import { ComposerCollectIntake } from "./ComposerCollectIntake";
import { DecisionCard } from "./DecisionCard";
import { ObjectiveReceipt } from "./ObjectiveReceipt";
import {
  answerInFlightOf,
  answeredCardsOf,
  cardsByAnswerMessage,
  openCardIdOf,
} from "./answeredCards";
import { resolveDecisionOptionLabel } from "./resolveDecisionOptionLabel";
import { resolveDecisionCardAction } from "./topicGateAction";
import { resolveExploreConfirmAction } from "./exploreConfirmAction";
import {
  type BuilderBusyCause,
  type BuilderLockCause,
  QuestionnaireBuilderPanel,
  emptyCanvasBusyLine,
  lockReasonFor,
} from "./QuestionnaireBuilderPanel";
import { CreationFlowStickyActionBar } from "@/features/studies/creation/components/CreationFlowStickyActionBar";
import { useStudyCreationBeforeUnload } from "@/features/studies/creation/hooks/useStudyCreationBeforeUnload";
import { useSidebarBackGuard } from "@/contexts/SidebarBackGuardContext";
import {
  carriesTestedAssets,
  diffTestedAssetsToOperations,
} from "../utils/draftOperations";
import {
  indexBlockingIssuesByQuestionId,
  shownAuthoringIssues,
} from "../utils/indexValidationIssues";
import { formatIssueCopy } from "../utils/questionnaireIssueCopy";
import {
  ASSET_MEDIA_ACCEPT,
  assetUploadRefusalLine,
  gateComposerMediaFiles,
  isSupportedVideoMime,
} from "../assets/assetMediaAccept";
import {
  assetKeysOf,
  attachCollectedMedia,
  attachPlacementsOf,
  attachItemsWithFileOf,
  attachReceiptLinesOf,
  carriesCollectedItems,
  collectableLinks,
  collectAssetsOperation,
  openAttachItemsOf,
  persistableCollectedMedia,
  receiptAssetKeysOf,
  uncoveredAssetsAmong,
  type CollectedAssetMediaRef,
} from "../assets/collectAssets";
import {
  mediaRefFromIssuedFile,
  mediaRefFromPdfPreview,
  mediaRefFromUpload,
  mediaRefFromVideoUrl,
} from "../assets/conceptAssets";
import { assetBindingNounWithArticle } from "../assets/assetRowLabel";
import {
  builderOptionLabel,
  builderOptionMark,
  chainCodesByMessage,
} from "../utils/builderCardCopy";
import { receiptItemCount } from "../utils/turnCommandCopy";
import { userMessageReceiptLines } from "../utils/userMessageReceiptLines";
import { handedOverLinks } from "../utils/handedOverLinks";
import { assetPanelLabel } from "../assets/conceptAssets";
import { useQuestionnaireCoverage } from "../hooks/useQuestionnaireCoverage";
import { useQuestionnaireDraftSession } from "../hooks/useQuestionnaireDraftSession";
import type { SelectedPopulation } from "../utils/draftAudience";
import {
  type StudyObjectiveUpdate,
  useQuestionnaireWorkspace,
  writeFailureCopy,
} from "../hooks/useQuestionnaireWorkspace";
import { resolveDraftForChatSend } from "./chatSendGuard";
import { ChatMessageCollect, ChatMessageOption } from "../types";

interface QuestionnaireBuilderChatProps {
  /** §0 goal id — seeds the chat's authored opening chain at creation. */
  goalId?: string | null;
  /** Flow-header chips (goal) rendered left of the actions. */
  headerSlot?: React.ReactNode;
  initialMessage?: string | null;
  language?: string;
  onEditAudience?: () => void;
  /**
   * Hydrates creation state from the loaded draft so ReviewStep prices from
   * the design its StudySpec resolved (`studySpec.resolved.design`).
   */
  onStudyDesign?: (design: StudyDesign | null) => void;
  /**
   * Enables the builder's language chip. Changing language after a draft
   * exists asks for confirmation, then runs an AI translation pass over the
   * current draft through the chat.
   */
  onLanguageChange?: (lang: string) => void;
  onSessionReady?: (sessionId: string) => void;
  /**
   * The Review compile: its survey, the draft revision it compiled, and that
   * revision's audience (what Review shows and launch reads).
   */
  onSurveyReady: (
    surveyJson: SurveyJson,
    revision: number,
    studyTitle?: string | null,
    audience?: QuestionnaireDraft["audience"] | null,
  ) => Promise<boolean>;
  resumeSessionId?: string | null;
  returnToStep?: number | null;
  onReturnToReview?: () => void;
  /**
   * The builder's "Save and continue" (`saveEditsIfConfirmed`), for a control
   * of the flow's own that leaves this draft (the "Continue draft" banner):
   * it resolves true when there was nothing to save or the save succeeded.
   * Set while the builder is mounted.
   */
  saveFirstRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
  selectedPopulation: SelectedPopulation | null;
  selectedStorage: { id: string; name: string } | null;
  studyType: StudyType;
}

// Option values for the asset-confirm card. A click on one runs a local action
// (add it as a family, put it on an item, or leave it out) instead of being
// sent to the planner.
const ASSET_CONFIRM_CANCEL = "__cancel_asset__";

/** The asset-confirm card's "Add as <family>" option. */
function addAsOptionValue(family: TestedAssetType): string {
  return `__add_asset_${family}__`;
}

/** The asset-confirm card's option that puts it on its `index`th item. */
function attachOptionValue(index: number): string {
  return `__attach_asset_${index}__`;
}

/** The asset-confirm card's option that puts several files on as many items. */
const ATTACH_EACH = "__attach_asset_each__";

/**
 * The asset-confirm card's option that uses the `index`th of the files or
 * links given, when they are more than the items take (one each, F19).
 */
function pickGivenOptionValue(index: number): string {
  return `__pick_given_${index}__`;
}

/** The family an "Add as" option adds, or null for any other option. */
function familyOfAddAsOption(value: string): TestedAssetType | null {
  return (
    (Object.keys(ASSET_FAMILY_FACTS) as Array<TestedAssetType>).find(
      (family) => addAsOptionValue(family) === value,
    ) ?? null
  );
}

// Option values for the local "update the questions?" card. Yes sends a typed
// `use_new_assets` command (plan S4.2, L24). The other option leaves the
// questions to the user: the asset still has to be in a question before
// Review (`tested_asset_unused`), so it never offers to leave it out.
const NEW_ASSETS_CONFIRM = {
  update: "__use_new_assets__",
  addMyself: "__add_to_questions_myself__",
} as const;

/**
 * What a card the builder added itself does when it is answered, kept by the
 * card's id: each card carries its own, so a later card never changes what an
 * earlier one sends.
 * - `add_asset`: the asset-confirm card, with the files or links handed
 *   over, how many each family it offers has room for (`room`), and the items
 *   it offers to put them on (`attachTo`: by option index for one, in order
 *   for several, every one when more were given than they take and the user
 *   picks the one to use). Its option is the one action: nothing is sent to
 *   the chat beside it.
 * - `use_new_assets`: the "update the questions?" card, with the assets it
 *   offers to bring in.
 */
type LocalCardAction =
  | {
      kind: "add_asset";
      urls: Array<string>;
      files: Array<File>;
      room: Partial<Record<TestedAssetType, number>>;
      attachTo: Array<MaterialAttachOffer>;
    }
  | { kind: "use_new_assets"; assetKeys: Array<string> };

/** The family registry's prose noun for an asset type (plan S3.4 row 39). */
function assetFamilyNoun(assetType: string): string {
  return isAssetFamily(assetType)
    ? ASSET_FAMILY_FACTS[assetType].nouns.singular
    : assetType;
}

interface CaptureFailure {
  url: string;
  reason?: string | null;
  detail?: string | null;
}

/**
 * What to say for links that could not be captured: each link with the one
 * reason its capture failed, in the link field's own words
 * (`friendlyCaptureError`; EX-22 read a sentence hedging between two), then
 * where a screenshot of it goes. `target` carries its article from the one
 * helper ("as an ad", "to \"Berry Spoon\""): QA4 AD N6 read "I'll add it as
 * a ad".
 */
function captureFailurePrompts(
  failures: Array<CaptureFailure>,
  target: string,
): Array<string> {
  return failures.map(
    ({ url, reason, detail }) =>
      `I didn't add ${url}. ${friendlyCaptureError(reason, detail)} Drop a screenshot in here and I'll add it ${target}.`,
  );
}

// Shown when the advance is refused and the backend gave no issue text for it —
// i.e. the compile itself failed. It names the retry the user can actually
// perform, because clicking Review again always goes back to the compiler;
// "try saving again" pointed at a save that had already succeeded and would not
// have helped.

function isUnansweredDecision(
  message: QuestionnaireChatMessage,
  isWaiting: (message: QuestionnaireChatMessage) => boolean,
): message is QuestionnaireChatMessage & {
  options: NonNullable<QuestionnaireChatMessage["options"]>;
} {
  return (
    message.role === "assistant" &&
    Array.isArray(message.options) &&
    message.options.length > 0 &&
    isWaiting(message)
  );
}

function isUnansweredCollect(
  message: QuestionnaireChatMessage,
  isWaiting: (message: QuestionnaireChatMessage) => boolean,
): message is QuestionnaireChatMessage & {
  collect: NonNullable<QuestionnaireChatMessage["collect"]>;
} {
  return (
    message.role === "assistant" && Boolean(message.collect) && isWaiting(message)
  );
}

/**
 * A message the builder added itself (`local_`): a note or a card, which has
 * no clarification record, or a turn that ended short. The server's
 * transcript never has it.
 */
function isLocalMessage(message: { id: string }): boolean {
  return message.id.startsWith("local_");
}

function findLastUnansweredComposerMessage(
  transcript: ReadonlyArray<QuestionnaireChatMessage>,
  isWaiting: (message: QuestionnaireChatMessage) => boolean,
): QuestionnaireChatMessage | null {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const message = transcript[i];
    if (
      isUnansweredDecision(message, isWaiting) ||
      isUnansweredCollect(message, isWaiting)
    ) {
      return message;
    }
  }
  return null;
}

interface ChatBubbleProps {
  aiIconSrc: string;
  /** The study's asset panel heading, for a reply's not-done lines (F16). */
  assetPanelHeading: string;
  /** The opening-chain step that asked this message, from its record. */
  chainCode?: string | null;
  isFirst: boolean;
  isLast: boolean;
  message: AssistantReplyMessage & Pick<QuestionnaireChatMessage, "role">;
  userInitials: string;
  whiteLabelEnabled: boolean;
}

/** The Review button's blocked reason, for `aria-describedby`. */
const REVIEW_BLOCKED_REASON_ID = "questionnaire-review-blocked-reason-text";

interface MobileDrawerProps {
  children: React.ReactNode;
  onClose: () => void;
  open: boolean;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5">
      {[0, 150, 300].map((delay) => (
        <div
          key={delay}
          className="h-2 w-2 rounded-full bg-muted-foreground/60 motion-safe:animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  );
}

function ChatBubble(props: ChatBubbleProps) {
  const {
    aiIconSrc,
    assetPanelHeading,
    chainCode,
    isFirst,
    isLast,
    message,
    userInitials,
    whiteLabelEnabled,
  } = props;
  const { t } = useTranslation("app");
  const isUser = message.role === "user";
  const displayContent = isUser
    ? message.content
    : assistantReplyLines(message, chainCode, assetPanelHeading, t).join("\n");

  return (
    <div
      data-testid={isUser ? "chat-message-user" : "chat-message-assistant"}
      className={cn(
        "flex gap-2",
        isUser ? "flex-row-reverse pl-[72px]" : "flex-row pr-[60px]",
        isLast ? "animate-fade-in" : "",
      )}
    >
      {!isUser ? (
        <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-full bg-study-chat-ai-bubble flex items-center justify-center">
          <img
            src={aiIconSrc}
            alt="AI"
            className="h-full w-full object-contain"
            style={{ transform: "scale(1.3)" }}
          />
        </div>
      ) : (
        <UserAvatar initials={userInitials} size="sm" className="h-7 w-7" />
      )}

      <div
        className={cn("flex-1 min-w-0", isUser ? "text-right" : "text-left")}
      >
        <div
          className={cn(
            "inline-block rounded-2xl px-4 py-2.5 text-base leading-[1.6] text-left",
            // A long link has no break point of its own: it wraps inside the
            // bubble instead of running past it (QA4 AD N6).
            "max-w-full [overflow-wrap:anywhere]",
            isUser
              ? cn(
                  "rounded-tr-sm text-study-chat-user-fg",
                  whiteLabelEnabled
                    ? "bg-brand-accent/30"
                    : "bg-twin-chat-bubble/30",
                )
              : "rounded-tl-sm bg-study-chat-ai-bubble text-foreground",
          )}
        >
          <ReplyLines id={message.id} text={displayContent} />
        </div>
      </div>
    </div>
  );
}

export { formatClipDuration } from "@/components/chat/AttachmentTiles";

/**
 * Files as `AttachmentTiles` items, each with its preview: an object URL for
 * an image or a video (CreativeMedia branches on the mime), the first page
 * for a PDF, else none (the document icon). The sent-attachments bubble and
 * the composer's staged files read it.
 */
function useFileTiles(
  files: ReadonlyArray<File> | undefined,
): Array<AttachmentTileItem> {
  const [filePreviews, setFilePreviews] = useState<Array<string | null>>([]);

  useEffect(() => {
    if (!files || files.length === 0) {
      setFilePreviews([]);
      return;
    }
    let cancelled = false;
    const objectUrls: Array<string> = [];
    setFilePreviews(files.map(() => null));
    files.forEach((file, index) => {
      if (file.type.startsWith("image/") || isSupportedVideoMime(file.type)) {
        const objectUrl = URL.createObjectURL(file);
        objectUrls.push(objectUrl);
        setFilePreviews((cur) =>
          cur.map((p, i) => (i === index ? objectUrl : p)),
        );
      } else if (file.type === "application/pdf") {
        void renderPdfFirstPagePreview(file, 320)
          .then((url) => {
            if (!cancelled)
              setFilePreviews((cur) =>
                cur.map((p, i) => (i === index ? url : p)),
              );
          })
          .catch(() => {
            /* keep the document-icon fallback */
          });
      }
    });
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  return (files ?? []).map((file, index) => ({
    src: filePreviews[index] ?? "",
    mimeType: file.type || undefined,
    label: file.name,
  }));
}

/**
 * A user-side chat bubble showing the images/videos/PDFs the user attached
 * through the composer collect intake — mirrors how attachments read in a
 * normal user message (right-aligned, avatar on the right, thumbnails in the
 * bubble). Exported for direct test coverage.
 *
 * In-session it renders from the submitted `File`s; after a reload (when those
 * `File`s are gone) it falls back to `mediaUrls` resolved from the persisted
 * assets, so the sent attachments survive a refresh.
 */
export function SentAttachmentsBubble(props: {
  files?: Array<File>;
  /** Reload fallback: persisted src + mime, and the asset's name for its
   *  label. A hosted https video URL carries no mime, so the mimeType must
   *  ride along for the video predicate to work. */
  mediaUrls?: Array<{ src: string; mimeType?: string; label?: string }>;
  userInitials: string;
  isLast: boolean;
}) {
  const { files, mediaUrls, userInitials, isLast } = props;
  const fileTiles = useFileTiles(files);

  // Unified thumbnail list: files (with generated previews) in-session, then
  // stored media (the pages captured in-session, or everything after a reload).
  const items = [
    ...fileTiles,
    ...(mediaUrls ?? []).map((media) => ({
      src: media.src,
      mimeType: media.mimeType,
      label: media.label,
    })),
  ];

  if (items.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-row-reverse gap-2 pl-[72px]",
        isLast && "animate-fade-in",
      )}
    >
      <UserAvatar initials={userInitials} size="sm" className="h-7 w-7" />
      <AttachmentTiles items={items} />
    </div>
  );
}

/**
 * The workspace on a phone: the design system's Sheet, so it is a dialog
 * with its focus trap, Escape and a close control (B22).
 */
function MobileDrawer(props: MobileDrawerProps) {
  const { children, onClose, open } = props;
  const { t } = useTranslation("app");

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="bottom"
        aria-describedby={undefined}
        className="top-[12%] gap-0 rounded-t-2xl p-0 lg:hidden"
      >
        <SheetHeader className="border-b border-border px-4 py-3 text-left">
          <SheetTitle className="text-sm font-medium">
            {t("questionnaire.workspace")}
          </SheetTitle>
        </SheetHeader>
        <div className="h-[calc(100%-53px)] min-h-0">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

export function QuestionnaireBuilderChat(props: QuestionnaireBuilderChatProps) {
  const {
    goalId,
    headerSlot,
    initialMessage,
    language,
    onStudyDesign,
    onEditAudience,
    onLanguageChange,
    onSessionReady,
    onSurveyReady,
    resumeSessionId,
    returnToStep,
    onReturnToReview,
    saveFirstRef,
    selectedPopulation,
  } = props;

  const { t } = useTranslation("app");
  const [didAdvance, setDidAdvance] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // The option picked on a card the builder added itself (a `local_` prompt).
  // Server cards are answered through their clarification records (S4.1).
  const [localCardAnswers, setLocalCardAnswers] = useState<
    Map<string, string>
  >(new Map());
  const [invitePaste, setInvitePaste] = useState(false);
  const [isOpeningWorkspace, setIsOpeningWorkspace] = useState(false);
  // What each card the builder added itself does when answered, by card id
  // (`LocalCardAction`). A pasted link or a dropped file waits on its
  // asset-confirm card, so a stray URL or image never silently becomes an
  // asset.
  const [localCardActions, setLocalCardActions] = useState<
    Map<string, LocalCardAction>
  >(new Map());
  // The assets whose "update the questions?" card waits for a chat question
  // to close (`offerQuestionUpdate` offers nothing while one is open). Not
  // kept across a reload, like the card itself.
  const [heldNewAssetKeys, setHeldNewAssetKeys] = useState<Array<string>>(
    [],
  );
  const [inputOtherExpanded, setInputOtherExpanded] = useState(false);
  const [inputOtherText, setInputOtherText] = useState("");
  // Files submitted through the composer intake, by collect message id — the
  // answered CollectCard shows their thumbnails in the chat history.
  const [collectFiles, setCollectFiles] = useState<
    Map<
      string,
      { files: Array<File>; mediaUrls: Array<{ src: string; mimeType?: string }> }
    >
  >(new Map());
  // True while a chat-side write that carries a video is in flight: the Save
  // watches it, and that wait is said out loud rather than left silent.
  const [readingCreative, setReadingCreative] = useState(false);
  // Chat-side asset writes (`POST /operations`) run outside a chat turn, so `sending`
  // does not cover them. While one is in flight, panel edits race the write:
  // the save-race keeps the newer local draft, the write's block wins on the
  // server, and the two disagree until a reload. Counted, not boolean, because
  // a collect submit and a composer add can overlap.
  const [assetWritesInFlight, setAssetWritesInFlight] = useState(0);
  // The collect intake holds files or links the user has not added yet.
  const [intakeStaged, setIntakeStaged] = useState(false);
  // Files the user attached in the composer while no upload card is open
  // (B2): staged beside their words, which stay, and never uploaded on their
  // own. A send takes them: with no words, the material card asks what they
  // are; with words, they go where the words go once their turn ends
  // (`composerTurnEnded`, L25): to the upload card the reply opens (its
  // intake's `initialFiles`, the one upload path), else to the material card.
  const [composerFiles, setComposerFiles] = useState<Array<File>>([]);
  const composerFileTiles = useFileTiles(composerFiles);
  // Set when a composer turn that carried staged files ends (L25).
  const [composerTurnEnded, setComposerTurnEnded] = useState(false);
  // The panel's Detect skip logic, from its request until its result opens:
  // the rules it returns are edits on the draft it read.
  const [detectingSkipLogic, setDetectingSkipLogic] = useState(false);
  // The collect card whose intake is writing its answer: its files upload and
  // its assets save before the receipt turn goes out (plan S4.1).
  const [intakeAnsweringCardId, setIntakeAnsweringCardId] = useState<
    string | null
  >(null);

  // Language picked while a draft already exists — held until the user
  // confirms the translation pass in the dialog below.
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  // The language the dialog names, kept through its closing animation: the
  // pick itself is cleared the moment it is confirmed or dismissed, and the
  // copy read "…in ." while the dialog faded (QA4 BR N9).
  const [dialogLanguage, setDialogLanguage] = useState<string | null>(null);
  if (pendingLanguage !== null && pendingLanguage !== dialogLanguage) {
    setDialogLanguage(pendingLanguage);
  }

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // The composer's visible attach control (B2): its files take the drop and
  // paste path (`handleComposerMediaFiles`), on every template and turn.
  const composerFileInputRef = useRef<HTMLInputElement>(null);

  const userProfile = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const rawUser = localStorage.getItem("user");
      if (!rawUser) {
        return null;
      }

      return JSON.parse(rawUser) as {
        avatarSeed?: string;
        email?: string;
        name?: string;
      };
    } catch {
      return null;
    }
  }, []);

  const avatarSeed =
    userProfile?.avatarSeed ??
    userProfile?.email ??
    userProfile?.name ??
    "user";
  const userInitials = useMemo(
    () => getUserInitials(userProfile?.name, userProfile?.email),
    [userProfile?.email, userProfile?.name],
  );

  const {
    appendAssistantPrompt,
    assumptions,
    cancelRequest,
    canStop,
    compiledSurveyJson,
    compilePersistedDraft,
    readiness,
    draftId,
    input,
    isDraftReady,
    isSavingStudyGoals,
    loadingDraft,
    canRetryLast,
    notEditableReason,
    messages,
    draft,
    persistDraftEdits,
    retryLast,
    saveStudyGoals,
    sendMessage,
    sendCommand,
    sending,
    sendUnconfirmed,
    sessionError,
    setInput,
    showSlowWarning,
    persistOperations,
    draftRevision,
    startManualDraft,
    studyGoals,
    clarifications,
    declaredAssetTypes: serverDeclaredAssetTypes,
    inFlightTurn,
    turnStage,
    turnStageTotal,
    stopRequested,
    answerWithNextAudienceChange,
  } = useQuestionnaireDraftSession({
    goalId,
    initialMessage,
    language,
    onSessionReady,
    resumeDraftId: resumeSessionId,
    selectedPopulation,
  });

  // The design the draft's StudySpec resolved (plan S3.4 row 38). Nest
  // re-resolves it on every commit, so each loaded or saved draft reprices.
  const hasDraft = Boolean(draft);
  const specDesign = studySpecOf(draft)?.resolved.design ?? null;

  // The record the turn in flight answers reads as answered while it runs
  // (plan S4.1): the record is still open until the turn commits, and the
  // user has already given this answer. A collect card's intake writing its
  // answer, before that turn goes out, is answering it too.
  const answerInFlight = useMemo(
    () =>
      answerInFlightOf(
        messages,
        clarifications,
        inFlightTurn,
        intakeAnsweringCardId,
      ),
    [messages, clarifications, inFlightTurn, intakeAnsweringCardId],
  );
  const shownClarifications = answerInFlight.clarifications;
  // How each server card was answered, and which one is waiting, read from
  // the transcript's clarification records (plan S4.1), never from the
  // content of a neighbouring message.
  const answeredCards = useMemo(
    () => answeredCardsOf(messages, shownClarifications),
    [messages, shownClarifications],
  );
  const answeredByMessage = useMemo(
    () => cardsByAnswerMessage(answeredCards),
    [answeredCards],
  );
  // The opening-chain step that asked each card, so the card is worded in
  // the interface language from its code and option ids.
  const chainCodes = useMemo(
    () => chainCodesByMessage(clarifications),
    [clarifications],
  );
  const openCardId = openCardIdOf(shownClarifications);
  // Whether a question waits on an answer is the open record (L20), whatever
  // asked it and whether or not the draft is built, unless the turn in
  // flight is answering it.
  const clarificationOpen =
    openClarificationOf({ clarifications: shownClarifications }) !== null;
  // A card shows its answered state once its record is answered (a local
  // card once it is picked); a superseded card stays unanswered.
  const answeredCardIds = useMemo(() => {
    const ids = new Set(localCardAnswers.keys());
    for (const [cardId, answer] of answeredCards) {
      if (answer.answered) ids.add(cardId);
    }
    return ids;
  }, [answeredCards, localCardAnswers]);
  // A local card waits until it is picked. The "update the questions?" card
  // also waits only while one of its assets is still in no question, by the
  // rule that offered it (`uncoveredAssetsAmong` on the saved draft): once
  // the asset is removed, or a Save puts it in a question, it asks nothing
  // (QA4 EX2 N5: it stayed live after the new ad was removed).
  const isWaitingCard = useCallback(
    (message: { id: string }) => {
      if (!isLocalMessage(message)) return message.id === openCardId;
      if (localCardAnswers.has(message.id)) return false;
      const action = localCardActions.get(message.id);
      return (
        action?.kind !== "use_new_assets" ||
        uncoveredAssetsAmong(action.assetKeys, { draft, readiness }).length > 0
      );
    },
    [draft, localCardActions, localCardAnswers, openCardId, readiness],
  );
  /** The answer a UI intercept gives the open card, by id (plan S4.1). */
  const interceptAnswer = useCallback(
    (card: { id: string; options?: Array<ChatMessageOption> }, value: string) => {
      const open = clarifications.find((record) => record.status === "open");
      if (!open || open.messageId !== card.id) return undefined;
      const optionId = card.options?.find((option) => option.value === value)?.id;
      return { clarificationId: open.id, ...(optionId ? { optionId } : {}) };
    },
    [clarifications],
  );

  useEffect(() => {
    if (!hasDraft) {
      return;
    }
    onStudyDesign?.(specDesign);
  }, [hasDraft, onStudyDesign, specDesign]);

  // Armed on first render when review sent us here with a prefilled re-plan.
  // Parent clears initialMessage after mount; the ref must not reset.
  const shouldReturnAfterReplanRef = useRef(
    Boolean(returnToStep && initialMessage?.trim()),
  );
  const hydratedCompileRef = useRef<SurveyJson | null | undefined>(undefined);
  const returnedToReviewRef = useRef(false);
  const replanSendStartedRef = useRef(false);

  useEffect(() => {
    if (!shouldReturnAfterReplanRef.current) {
      return;
    }
    if (hydratedCompileRef.current === undefined && compiledSurveyJson) {
      hydratedCompileRef.current = compiledSurveyJson;
    }
  }, [compiledSurveyJson]);

  useEffect(() => {
    if (
      !shouldReturnAfterReplanRef.current ||
      !onReturnToReview ||
      sending ||
      returnedToReviewRef.current
    ) {
      return;
    }
    if (!readiness?.canCompile || !compiledSurveyJson) {
      return;
    }
    if (compiledSurveyJson === hydratedCompileRef.current) {
      return;
    }
    returnedToReviewRef.current = true;
    void (async () => {
      // Review returns with the compile of the persisted draft and its
      // revision, as the Review click does, never the cached preview.
      const compiled = await compilePersistedDraft();
      const success =
        compiled.surveyJson !== null &&
        compiled.revision !== null &&
        (await onSurveyReady(
          compiled.surveyJson,
          compiled.revision,
          draft?.title ?? null,
          compiled.audience,
        ));
      if (success) {
        onReturnToReview();
        return;
      }
      returnedToReviewRef.current = false;
    })();
  }, [
    compiledSurveyJson,
    compilePersistedDraft,
    draft?.title,
    onReturnToReview,
    onSurveyReady,
    readiness?.canCompile,
    sending,
  ]);

  useEffect(() => {
    if (!shouldReturnAfterReplanRef.current) {
      return;
    }
    if (sessionError) {
      shouldReturnAfterReplanRef.current = false;
      return;
    }
    if (sending) {
      replanSendStartedRef.current = true;
      return;
    }
    if (!replanSendStartedRef.current) {
      return;
    }
    const hasNewCompile =
      compiledSurveyJson != null &&
      compiledSurveyJson !== hydratedCompileRef.current;
    if (!hasNewCompile) {
      shouldReturnAfterReplanRef.current = false;
    }
  }, [compiledSurveyJson, sending, sessionError]);

  const { user: authUser } = useAuth();
  const whiteLabelEnabled =
    (authUser?.featureFlags as { whiteLabel?: boolean } | undefined)
      ?.whiteLabel === true;
  // Always the Fairgen twins icon — this AI avatar is never brand-swapped, even for themed accounts.
  const aiIconSrc = "/sidebar-brand-logo.svg";

  const workspace = useQuestionnaireWorkspace({
    assumptions,
    onSaveDraft: persistDraftEdits,
    selectedPopulation,
    serverDraft: draft,
    serverRevision: draftRevision,
    serverSurveyJson: compiledSurveyJson,
  });
  // The study's asset panel, named as the panel heads itself: where a reply
  // points for what the chat could not do (F16).
  const assetPanelHeading = assetPanelLabel(
    workspace.conceptAssets,
    workspace.currentDraft?.testedAssets?.assetTypes ?? [],
    t,
  ).plural;
  // What a pasted link or a dropped file may become: the study's families,
  // from their one owner (`studyMaterialFamilies`, owner decision B2-4). The
  // recipe the saved draft records, else the goal's (before a draft has a
  // spec), and the families the catalog declares: the workspace's block once
  // there is one (a panel type switch lands there first), else the server
  // draft's, as on an intake draft with no workspace. CT-11: a concept study
  // was offered "Add as an ad / landing page / package".
  const declaredTypes =
    workspace.currentDraft?.testedAssets?.assetTypes ?? serverDeclaredAssetTypes;
  const studyFamilies = useMemo(
    () =>
      studyMaterialFamilies({
        recipe:
          studySpecOf(draft)?.recorded.recipe ??
          (goalId ? goalRecipe(goalId) : null),
        declaredTypes,
      }),
    [declaredTypes, draft, goalId],
  );
  // The open card collects pasted material (claims, messages, pains,
  // concepts): typed words, links included, are its answer.
  const openCardCollectsText =
    openClarificationOf({ clarifications: shownClarifications })?.pending
      ?.kind === "extract_material";

  const assetWritesPending =
    assetWritesInFlight > 0 || workspace.assetUploadsInFlight;

  // An asset-confirm card waits on its answer: what was pasted or dropped
  // rides on it.
  const assetCardOpen = useMemo(
    () =>
      [...localCardActions].some(
        ([cardId, action]) =>
          action.kind === "add_asset" && !localCardAnswers.has(cardId),
      ),
    [localCardActions, localCardAnswers],
  );

  // What leaving the page would lose, for the page's leave guards (the
  // browser's "Leave site?" and the sidebar's): the workspace's unsaved edits,
  // and an intake the user staged and has not added (the collect intake's
  // files or links, the composer's files, or a pasted file waiting on its
  // confirm card). Nothing else is: only the explicit Save writes, the draft
  // is otherwise persisted, the composer's unsent words are kept for the tab,
  // and a turn runs on the server.
  const objectiveEditorSaveRef = useRef<(() => Promise<boolean>) | null>(null);
  const savingObjectiveBufferRef = useRef(false);
  const [objectiveEditorOpen, setObjectiveEditorOpen] = useState(false);
  const onObjectiveEditorChange = useCallback(
    (editing: boolean, savePendingEdit?: () => Promise<boolean>) => {
      objectiveEditorSaveRef.current = editing ? savePendingEdit ?? null : null;
      setObjectiveEditorOpen(editing);
    },
    [],
  );
  const hasUnsavedWork =
    workspace.isDirty ||
    objectiveEditorOpen ||
    intakeStaged ||
    composerFiles.length > 0 ||
    assetCardOpen;
  useStudyCreationBeforeUnload(hasUnsavedWork);
  useSidebarBackGuard(hasUnsavedWork);

  // The builder's "update the questions?" card, for the assets a write just
  // added that the write's readiness names as in no question
  // (`tested_asset_unused`, 04 I-19). One rule for every road that writes
  // assets, run once where every commit lands (`adoptedAssetKeysRef` below):
  // an asset the questions already take in gets no card. While a chat question waits on its answer the card
  // would take that question's place, so it is held until the question
  // closes (`heldNewAssetKeys`) and then asks again, by this same rule. The
  // card carries its own asset keys: Yes sends `use_new_assets` for exactly
  // those.
  const offerQuestionUpdate = useCallback(
    (
      addedKeys: ReadonlyArray<string>,
      accepted: {
        draft: unknown;
        readiness?: QuestionnaireDraftReadiness | null;
      },
    ) => {
      const uncovered = uncoveredAssetsAmong(addedKeys, accepted);
      if (uncovered.length === 0) return;
      if (clarificationOpen) {
        setHeldNewAssetKeys((current) => [
          ...new Set([
            ...current,
            ...uncovered.map((asset) => asset.stableKey),
          ]),
        ]);
        return;
      }
      const [{ type }] = uncovered;
      const count = uncovered.length;
      const oneFamily =
        isAssetFamily(type) && uncovered.every((asset) => asset.type === type);
      const noun = (
        oneFamily
          ? count === 1
            ? t(`questionnaire.assetTypes.${type}.singular`)
            : t(`questionnaire.assetTypes.${type}.plural`)
          : count === 1
            ? t("questionnaire.assetsFallback.singular")
            : t("questionnaire.assetsFallback.plural")
      ).toLocaleLowerCase();
      const cardId = appendAssistantPrompt({
        content: t("questionnaire.newAssetsCard.prompt", { count, noun }),
        options: [
          {
            label: t("questionnaire.newAssetsCard.update"),
            value: NEW_ASSETS_CONFIRM.update,
          },
          {
            label: t("questionnaire.newAssetsCard.addMyself", { count }),
            value: NEW_ASSETS_CONFIRM.addMyself,
          },
        ],
      });
      setLocalCardActions((current) =>
        new Map(current).set(cardId, {
          kind: "use_new_assets",
          assetKeys: uncovered.map((asset) => asset.stableKey),
        }),
      );
    },
    [appendAssistantPrompt, clarificationOpen, t],
  );

  /**
   * The one rule after every commit that adds assets (BP-14), whichever road
   * wrote them: a Save, a chat-side asset write, an upload card's receipt
   * turn, or any other turn. The assets the adopted draft holds that the
   * draft adopted before it did not are offered the "update the questions?"
   * card (`offerQuestionUpdate`), read off the session's accepted draft and
   * its readiness, so no road runs it alone and none can skip it. The
   * page's load is the baseline, never an addition.
   */
  const adoptedAssetKeysRef = useRef<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (loadingDraft) return;
    const keys = assetKeysOf(draft?.testedAssets);
    const before = adoptedAssetKeysRef.current;
    adoptedAssetKeysRef.current = new Set(keys);
    if (before === null) return;
    const added = keys.filter((key) => !before.has(key));
    if (added.length > 0) offerQuestionUpdate(added, { draft, readiness });
  }, [draft, loadingDraft, offerQuestionUpdate, readiness]);

  /**
   * Every Save of the builder's edits, whichever road asks for it (the
   * panel's Save, "Save and continue" before a turn, Review, Edit audience or
   * an asset write). What it added gets the one post-commit rule above.
   */
  const saveEdits = useCallback(
    () => workspace.saveChanges(),
    [workspace],
  );

  // "Save and continue": a chat turn and Review work on the saved draft, so
  // unsaved edits are saved first, but only once the user says so. Resolves
  // true to save and continue, false to keep editing.
  const saveContinueResolverRef = useRef<((proceed: boolean) => void) | null>(
    null,
  );
  const [saveContinueOpen, setSaveContinueOpen] = useState(false);
  const confirmSaveAndContinue = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        // One question at a time: an earlier unanswered ask keeps editing.
        saveContinueResolverRef.current?.(false);
        saveContinueResolverRef.current = resolve;
        setSaveContinueOpen(true);
      }),
    [],
  );
  const settleSaveContinue = useCallback((proceed: boolean) => {
    const resolve = saveContinueResolverRef.current;
    saveContinueResolverRef.current = null;
    setSaveContinueOpen(false);
    resolve?.(proceed);
  }, []);
  /**
   * Save unsaved edits before something that works on the saved draft, once
   * the user agrees. Resolves true when there was nothing to save or the
   * save succeeded.
   */
  const savePendingBuffers = useCallback(async (
    onWorkspaceSaved?: (saved: Awaited<ReturnType<typeof saveEdits>>) => void,
  ): Promise<boolean> => {
    const saveObjectiveEditor = objectiveEditorSaveRef.current;
    if (workspace.isDirty) {
      const saved = await saveEdits();
      if (!saved?.draft) return false;
      onWorkspaceSaved?.(saved);
    }
    if (!saveObjectiveEditor) return true;
    savingObjectiveBufferRef.current = true;
    try {
      return await saveObjectiveEditor();
    } finally {
      savingObjectiveBufferRef.current = false;
    }
  }, [saveEdits, workspace.isDirty]);
  const saveEditsIfConfirmed = useCallback(async (
    onWorkspaceSaved?: (saved: Awaited<ReturnType<typeof saveEdits>>) => void,
  ): Promise<boolean> => {
    if (!workspace.isDirty && !objectiveEditorSaveRef.current) return true;
    if (!(await confirmSaveAndContinue())) return false;
    return savePendingBuffers(onWorkspaceSaved);
  }, [confirmSaveAndContinue, savePendingBuffers, workspace.isDirty]);
  // Detect skip logic works on the saved draft: the panel asks the same
  // "Save and continue" first.
  const saveEditsFirst = useCallback(
    () => saveEditsIfConfirmed(),
    [saveEditsIfConfirmed],
  );
  // The flow's own control that leaves this draft (the "Continue draft"
  // banner) asks the same "Save and continue" first (F26 #11).
  useEffect(() => {
    if (!saveFirstRef) return undefined;
    saveFirstRef.current = saveEditsIfConfirmed;
    return () => {
      if (saveFirstRef.current === saveEditsIfConfirmed) {
        saveFirstRef.current = null;
      }
    };
  }, [saveEditsIfConfirmed, saveFirstRef]);
  // Editing the audience leaves the builder; unsaved edits are saved first,
  // or the user stays.
  const editAudience = useCallback(async () => {
    if (!onEditAudience) return;
    if (!(await saveEditsIfConfirmed())) return;
    onEditAudience();
  }, [onEditAudience, saveEditsIfConfirmed]);
  // The objective panel's Save is a `set_objective` turn (plan S4.2, L23),
  // so it works on the saved draft too. Resolves whether the turn committed:
  // false when nothing was sent, or the turn failed or was stopped, and the
  // row reopens its editor on the text.
  const saveObjectiveAfterEdits = useCallback(
    async (next: StudyObjectiveUpdate): Promise<boolean> => {
      if (
        !savingObjectiveBufferRef.current &&
        !(await saveEditsIfConfirmed())
      ) return false;
      return saveStudyGoals(next);
    },
    [saveEditsIfConfirmed, saveStudyGoals],
  );
  // The "Study brand" chip (plan Decision 5): the brand the saved draft's
  // spec resolves to, and the user's edit as one `set_study_brand` write on
  // the saved draft (unsaved edits are saved first).
  const studyBrand = useMemo(
    () => resolveStudyBrand(studySpecOf(draft)?.recorded.context),
    [draft],
  );
  const saveStudyBrand = useCallback(
    async (brandName: string | null): Promise<boolean> => {
      if (!(await saveEditsIfConfirmed())) return false;
      try {
        await persistOperations(
          [{ op: "set_study_brand", brandName }],
          // Replay rule: the latest draft already holds this exact user edit.
          (latest) =>
            latest.studyBrand?.source === "user_edit" &&
            (latest.studyBrand.brandName ?? null) === brandName,
        );
        return true;
      } catch {
        toast.error(t("questionnaire.studyBrand.saveFailed"));
        return false;
      }
    },
    [persistOperations, saveEditsIfConfirmed, t],
  );
  // A retry sends the failed turn again, on the saved draft.
  const retryLastAfterEdits = useCallback(
    () => retryLast(() => saveEditsIfConfirmed()),
    [retryLast, saveEditsIfConfirmed],
  );
  // The one busy fact the chat's controls lock on: something is writing the
  // draft (a turn, a Save, a chat-side asset write, or the request that opens
  // the workspace from Build manually / Paste), a skip-logic detection whose
  // rules will land as edits on the draft it read, or the page is still
  // loading it. The composer, the card's options, Edit audience, the language
  // pick, Review and the panel's own lock all read it, because each either
  // writes the draft itself or needs a Save that cannot run twice.
  // `sending` is the session's turn in flight (its `inFlightTurn`), live or
  // re-attached after a reload; the load is its own fact, so it shows no
  // typing dots or Stop and never unlocks a turn.
  //
  // The fact carries its cause, so a locked control can say what it waits
  // on. When two overlap (a collect receipt's turn during its asset write,
  // a turn after its "Save and continue"), the turn is named first.
  const busyCause: BuilderBusyCause | null = sending
    ? "turn"
    : workspace.isSaving
      ? "save"
      : assetWritesPending
        ? "asset_write"
        : isOpeningWorkspace
          ? "opening"
          : detectingSkipLogic
            ? "detecting"
            : loadingDraft
              ? "loading"
              : notEditableReason
                ? "read_only"
                : null;
  const chatInputDisabled = busyCause !== null;
  // A card held back by a chat question asks once that question has closed
  // and nothing runs (the turn that answers it has committed), for the held
  // assets the saved readiness still names as in no question.
  useEffect(() => {
    if (heldNewAssetKeys.length === 0 || clarificationOpen || busyCause) return;
    setHeldNewAssetKeys([]);
    offerQuestionUpdate(heldNewAssetKeys, { draft, readiness });
  }, [
    busyCause,
    clarificationOpen,
    draft,
    heldNewAssetKeys,
    offerQuestionUpdate,
    readiness,
  ]);
  // The composer's free text also waits while an asset-confirm card is open:
  // a link's words wait in the composer for that card's answer, so a second
  // message would go out ahead of them. The card's options stay live; they
  // read `chatInputDisabled`.
  const composerLockCause: BuilderLockCause | null =
    busyCause ?? (assetCardOpen ? "asset_card" : null);
  const buildProgressLine =
    busyCause === "turn"
      ? emptyCanvasBusyLine("turn", turnStage, {
          stopRequested,
          total: turnStageTotal,
        })
      : null;
  const composerLocked = composerLockCause !== null;

  // ── Coverage assessment ────────────────────────────────────────────────────
  // The server scores the accepted revision and caches its verdicts, so
  // coverage refreshes after a Save or an agent turn, never from unsaved edits.
  const coverageByQuestionId = useQuestionnaireCoverage({
    draftId,
    revision: draftRevision,
    questionIds: (
      (draft?.researchQuestions ?? []) as Array<{ id: string }>
    ).map((question) => question.id),
  });
  // ── End coverage assessment ────────────────────────────────────────────────

  const issueCopyContext = useMemo(
    () => ({
      resolvedMinimum: readiness?.resolvedMinimum,
      draftId,
    }),
    [draftId, readiness?.resolvedMinimum],
  );
  // The server's verdict, and its `clarification_open` still gates Review
  // and Launch. While that record is being answered (the turn in flight, or
  // the collect intake's write before it), the banner and Review's tooltip do
  // not ask the user for the answer again (plan S4.1).
  const shownBlockingIssues = useMemo(
    () =>
      answerInFlight.answering
        ? readiness?.blockingIssues?.filter(
            (issue) => issue.code !== "clarification_open",
          )
        : readiness?.blockingIssues,
    [answerInFlight.answering, readiness?.blockingIssues],
  );
  const indexedIssues = useMemo(() => {
    if (workspace.isDirty || !readiness) {
      return indexBlockingIssuesByQuestionId(undefined, null);
    }
    return indexBlockingIssuesByQuestionId(
      shownBlockingIssues,
      workspace.savedDraft ?? null,
    );
  }, [readiness, shownBlockingIssues, workspace.isDirty, workspace.savedDraft]);
  // The verdict's advice (QA4 N3): computed on every Save and shown nowhere
  // before. Indexed the same way, onto the questions and the items it names,
  // and read only while the draft on screen is the saved one it describes.
  const indexedAdvisories = useMemo(
    () =>
      workspace.isDirty || !readiness
        ? indexBlockingIssuesByQuestionId(undefined, null)
        : indexBlockingIssuesByQuestionId(
            readiness.advisoryIssues,
            workspace.savedDraft ?? null,
          ),
    [readiness, workspace.isDirty, workspace.savedDraft],
  );

  useEffect(() => {
    const issues = readiness?.blockingIssues;
    if (!issues?.length) {
      return;
    }
    for (const issue of issues) {
      const formatted = formatIssueCopy(issue, issueCopyContext);
      if (!formatted.mapped) {
        track(AnalyticsEvents.QUESTIONNAIRE_ISSUE_UNMAPPED, {
          code: formatted.code,
        });
      }
    }
  }, [issueCopyContext, readiness?.blockingIssues]);

  /**
   * Every chat turn goes out on the saved draft: unsaved edits are saved
   * first, once the user agrees. Nothing on the asset cards holds a turn
   * back: the server's applier and readiness are the one verdict on an
   * asset, and "remove claim 3" is how a blank card gets fixed (QA4 FE3).
   * `onBlocked` runs when the turn did not go out. Resolves whether the turn
   * committed: false when it was blocked, or it failed or was stopped.
   */
  const sendGuardedChatTurn = useCallback(
    async (
      message: string | { command: QuestionnaireTurnCommand },
      extras?: {
        onBlocked?: () => void;
        optionId?: string;
        /** The words are the composer's (the session's `sendMessage`). */
        from?: "composer";
      },
    ): Promise<boolean> => {
      if (assetWritesPending) {
        extras?.onBlocked?.();
        return false;
      }
      setInvitePaste(false);
      if (!(await saveEditsIfConfirmed())) {
        extras?.onBlocked?.();
        return false;
      }
      const result = await resolveDraftForChatSend(
        {
          ...workspace,
          // The shared continuation gate above has already saved every local
          // buffer it found, so this helper must not ask a second time from
          // the stale render that started the action.
          isDirty: false,
          saveChanges: saveEdits,
        },
        {
          confirmSave: confirmSaveAndContinue,
          onCancelled: () => extras?.onBlocked?.(),
          // saveChanges has already said why; a second toast would talk over it.
          onSaveFailure: () => extras?.onBlocked?.(),
        },
      );
      if (!result.ok) {
        return false;
      }
      return typeof message === "string"
        ? sendMessage(message, extras?.optionId, extras?.from)
        : sendCommand(message.command);
    },
    [
      assetWritesPending,
      confirmSaveAndContinue,
      saveEdits,
      saveEditsIfConfirmed,
      sendCommand,
      sendMessage,
      workspace,
    ],
  );

  // The questionnaire's language is the committed draft's (`contentLanguage`,
  // stamped by the build and by a translation's commit). The chip, Review and
  // the launch read creation state, so it takes the draft's language whenever
  // a committed draft names a new one, including a translation that settles
  // after a reload. Until a draft names one, the pick below stands.
  const committedLanguage = draft?.contentLanguage;

  /**
   * Language change guard. Locked while anything writes the draft (a turn
   * above all): that turn commits in the language it was sent with. Before
   * the draft names a language the pick is free (it is the language the
   * build writes in). Once it does, the pick is a `translate` turn, which
   * the draft records at commit: with questions it is an AI translation pass
   * (confirm first), with none the server records the language and no model
   * runs (C12-2).
   */
  const handleLanguageSelect = useCallback(
    (lang: string) => {
      if (!onLanguageChange || lang === language) {
        return;
      }
      if (chatInputDisabled) {
        toast.info("Hang on — let the current response finish first.");
        return;
      }
      if (workspace.questionCount === 0) {
        if (committedLanguage) {
          void sendGuardedChatTurn({
            command: { kind: "translate", language: lang },
          });
        } else {
          onLanguageChange(lang);
        }
        return;
      }
      setPendingLanguage(lang);
    },
    [
      chatInputDisabled,
      committedLanguage,
      language,
      onLanguageChange,
      sendGuardedChatTurn,
      workspace.questionCount,
    ],
  );

  // A typed command (plan S4.2): what translating means is the revision
  // prompt's to say, not a sentence the builder writes. It works on the saved
  // draft, so unsaved edits are saved first (Save and continue). The language
  // does not change here: the translation records it on the draft at commit,
  // and the builder takes it from the committed draft (below), so a failed,
  // stopped or blocked translation leaves it as it was.
  const confirmLanguageTranslate = useCallback(() => {
    if (!pendingLanguage) {
      return;
    }
    const nextLanguage = pendingLanguage;
    setPendingLanguage(null);
    void sendGuardedChatTurn({
      command: { kind: "translate", language: nextLanguage },
    });
  }, [pendingLanguage, sendGuardedChatTurn]);

  // The one derivation from the committed draft to creation state (C12-2).
  useEffect(() => {
    if (!committedLanguage) {
      return;
    }
    onLanguageChange?.(committedLanguage);
  }, [committedLanguage, onLanguageChange]);

  /**
   * The asset-intake core for BOTH the opening collect card and the composer
   * (a typed URL, a pasted or dropped file), D2 S5.4. The browser hands over
   * bytes and links; the server names, reads and reports what it collected:
   * - each file is uploaded and issued (a PDF with its page-1 preview);
   * - each page link is captured once for its preview and issued, so the Save
   *   never captures it again; a direct video link goes as a link;
   * - the new assets are one `collect_assets` operation, applied by the server
   *   (`asset_collected` and the links it skipped come back as effects), or
   *   carried by the card's durable receipt (`deferPersist`);
   * - an attach card (F19) puts each file on its item's assets instead.
   * Unsaved edits are saved first, once the user agrees ("Save and continue").
   * It does NOT refresh the UI or send chat: the caller decides.
   */
  const addCollectedAssets = useCallback(
    async (
      collect: Pick<ChatMessageCollect, "assetType" | "maxAssets" | "attachTo">,
      values: {
        urls: Array<string>;
        files: Array<File>;
        /** Pages the intake captured for their preview, each issued as a file. */
        captured?: Array<{ url: string; file: IssuedAssetFile }>;
        /** On an attach card: the item each file goes on, file by file. */
        targets?: Array<ChatMessageCollectAttachItem>;
      },
      deferPersist = false,
    ): Promise<
      | {
          currentDraft: QuestionnaireDraft | null;
          /**
           * The saved catalog the write started from, or the accepted one
           * after an immediate write. `currentDraft` is null until the first
           * instrument is built.
           */
          assetCatalog: TestedAssetBlock | null;
          revision?: number;
          /** The keys of the assets this write added (its `asset_collected` effects). */
          addedAssetKeys: Array<string>;
          failedCaptures: Array<CaptureFailure>;
          /**
           * Links refused with a written reason (a video host we cannot
           * read). Separate from `failedCaptures`, which is a capture that was
           * attempted and came back unusable: these were never worth attempting.
           */
          refusedUrls: Array<{ url: string; reason: string }>;
          /**
           * What the Save of the collect reported (`asset_collected`, and a
           * link it skipped). Empty when the receipt turn carries the collect:
           * its stored message carries them, shown with it in the transcript.
           */
          effects: Array<QuestionnaireOperationEffect>;
          /** On an attach write: the items it put a file on (F19). */
          attachedItems?: Array<ChatMessageCollectAttachItem>;
          /** Typed asset mutations carried by the durable receipt turn. */
          assetOperations?: Array<QuestionnaireCollectedAssetOperation>;
          blocked?: never;
        }
      | { blocked: "no_draft" | "save_failed" | "cancelled" }
    > => {
      if (!draftId) return { blocked: "no_draft" };
      // The saved draft, never the builder's unsaved copy.
      let baseDraft = draft ?? workspace.savedDraft ?? null;
      let savedForContinuation:
        | Awaited<ReturnType<typeof saveEdits>>
        | undefined;
      if (!(await saveEditsIfConfirmed((saved) => {
        savedForContinuation = saved;
      }))) {
        return { blocked: "cancelled" };
      }
      if (savedForContinuation?.draft) {
        baseDraft = savedForContinuation.draft;
      }
      const savedBlock =
        (baseDraft?.testedAssets as TestedAssetBlock | undefined) ?? null;
      const failedCaptures: Array<CaptureFailure> = [];
      const refusedUrls: Array<{ url: string; reason: string }> = [];
      // In the order the assets are named: video links, files, page captures.
      const videoItems: Array<CollectedAssetMediaRef & { sourceUrl: string }> = [];
      const fileItems: Array<CollectedAssetMediaRef> = [];
      const captureItems: Array<CollectedAssetMediaRef & { sourceUrl: string }> = [];
      for (const url of collectableLinks(values.urls)) {
        // FIRST, before any capture. A Vimeo / Drive / Dropbox link screenshots
        // as a still of a player page: name the host and say what to do.
        const blocked = nonKeyframeableVideoMessage(url);
        if (blocked) {
          refusedUrls.push({ url, reason: blocked });
          continue;
        }
        // A link straight at video bytes IS the asset: nothing to capture.
        const videoRef = mediaRefFromVideoUrl(url);
        if (videoRef) {
          videoItems.push({ ...videoRef, sourceUrl: url });
          continue;
        }
        try {
          const capture = await captureQuestionnaireUrlApi(url, {
            scale: 1,
            fullPage: true,
          });
          if (capture.ok && capture.file) {
            captureItems.push({
              ...mediaRefFromIssuedFile(capture.file),
              sourceUrl: url,
            });
          } else {
            failedCaptures.push({
              url,
              reason: capture.failureReason,
              detail: capture.detail,
            });
          }
        } catch (error) {
          const detail = error instanceof Error ? error.message : String(error);
          console.warn("[questionnaire-chat] URL capture failed", {
            url,
            message: detail,
          });
          failedCaptures.push({ url, reason: "error", detail });
        }
      }
      for (const { url, file } of values.captured ?? []) {
        captureItems.push({ ...mediaRefFromIssuedFile(file), sourceUrl: url });
      }
      for (const file of values.files) {
        // The upload policy, before the bytes leave (B23): a refused file is
        // named with its reason, and the rest are still collected.
        const refusal = assetUploadRefusalLine(file);
        if (refusal) {
          toast.error(refusal);
          continue;
        }
        const upload = await uploadQuestionnaireAssetFileApi(file);
        const mediaRef = mediaRefFromUpload(upload, file);
        // A PDF's page 1, rendered and issued by the upload.
        fileItems.push({
          ...mediaRef,
          ...(upload.preview
            ? {
                clientPreviewMediaRefs: [
                  mediaRefFromPdfPreview(upload.preview, mediaRef),
                ],
              }
            : {}),
        });
      }
      const nothingCollected = {
        currentDraft: null,
        assetCatalog: savedBlock,
        addedAssetKeys: [],
        failedCaptures,
        refusedUrls,
        effects: [],
      };
      // An attach card (F19): each file becomes the media of every asset of
      // the item the user put it on (each price version of a product alike),
      // so the write is only those assets' media and nothing is added. With
      // nothing staged it writes nothing: the user continued without a file.
      if (collect.attachTo) {
        const placements = attachPlacementsOf(
          savedBlock,
          collect,
          [...videoItems, ...fileItems, ...captureItems],
          values.targets,
        );
        const preparedBlock = savedBlock
          ? (attachCollectedMedia(
              savedBlock as unknown as Record<string, unknown>,
              placements,
            ) as unknown as TestedAssetBlock)
          : null;
        const operations = (
          savedBlock && preparedBlock
            ? diffTestedAssetsToOperations(savedBlock, preparedBlock)
            : []
        ) as Array<QuestionnaireCollectedAssetOperation>;
        const accepted =
          !deferPersist && operations.length > 0
            ? await persistOperations(
                operations,
                carriesTestedAssets(preparedBlock),
              )
            : { draft: baseDraft, revision: undefined };
        return {
          ...nothingCollected,
          currentDraft: accepted.draft,
          revision: accepted.revision,
          assetCatalog: deferPersist
            ? preparedBlock
            : (accepted.draft?.testedAssets as TestedAssetBlock | undefined) ??
              null,
          attachedItems:
            operations.length > 0 ? placements.map(({ item }) => item) : [],
          ...(deferPersist ? { assetOperations: operations } : {}),
        };
      }
      const items: Array<QuestionnaireCollectedItem> = [
        ...videoItems.map(({ sourceUrl }) => ({ url: sourceUrl })),
        ...fileItems.map((mediaRef) => ({
          media: persistableCollectedMedia(mediaRef),
        })),
        ...captureItems.map(({ sourceUrl, ...mediaRef }) => ({
          media: persistableCollectedMedia(mediaRef),
          url: sourceUrl,
        })),
      ];
      if (items.length === 0) return nothingCollected;
      const operation = collectAssetsOperation(collect, items);
      // The durable receipt carries the operation; the turn applies and
      // reads it before PLAN, in one revision with the receipt.
      if (deferPersist) {
        return {
          ...nothingCollected,
          currentDraft: baseDraft,
          assetOperations: [operation],
        };
      }
      // A Save that carries a video watches it: the existing "reading the
      // ad" line covers the wait.
      const watches = items.some(
        (item) =>
          "media" in item
            ? item.media.some((ref) => ref.kind === "video")
            : true,
      );
      if (watches) setReadingCreative(true);
      let accepted: Awaited<ReturnType<typeof persistOperations>>;
      try {
        accepted = await persistOperations(
          [operation],
          carriesCollectedItems(items),
        );
      } finally {
        if (watches) setReadingCreative(false);
      }
      const effects = accepted.effects ?? [];
      return {
        currentDraft: accepted.draft,
        revision: accepted.revision,
        assetCatalog:
          (accepted.draft?.testedAssets as TestedAssetBlock | undefined) ??
          null,
        addedAssetKeys: effects.flatMap((effect) =>
          effect.kind === "asset_collected" && effect.assetStableKey
            ? [effect.assetStableKey]
            : [],
        ),
        failedCaptures,
        refusedUrls,
        effects,
      };
    },
    [draft, draftId, persistOperations, saveEditsIfConfirmed, workspace],
  );

  /**
   * What the server's collect skipped (D2): a video host it cannot read, or a
   * link already attached. One path for both answers a collect gets: the
   * Save's effects, and the effects its durable receipt's message carries.
   * Says nothing when it skipped nothing; returns whether it said anything.
   */
  // A Save's skips (it writes no message); a receipt turn's are its stored
  // message's, shown with it in the transcript.
  const sayCollectedSkips = useCallback(
    (effects: ReadonlyArray<QuestionnaireOperationEffect>): boolean => {
      const lines = userMessageReceiptLines({ effects }, t);
      for (const content of lines) appendAssistantPrompt({ content });
      return lines.length > 0;
    },
    [appendAssistantPrompt, t],
  );

  const handleCollectSubmit = useCallback(
    async (
      message: { id: string; collect: ChatMessageCollect },
      values: {
        urls: Array<string>;
        files: Array<File>;
        captured?: Array<{ url: string; file: IssuedAssetFile }>;
        targets?: Array<ChatMessageCollectAttachItem>;
      },
    ) => {
      if (!draftId) return;
      setAssetWritesInFlight((n) => n + 1);
      setIntakeAnsweringCardId(message.id);
      try {
        const result = await addCollectedAssets(message.collect, {
          urls: values.urls,
          files: values.files,
          captured: values.captured,
          targets: values.targets,
        }, true);
        // Nothing was collected: the intake keeps what the user staged. A
        // failed save has already said why.
        if (result.blocked === "cancelled" || result.blocked === "save_failed")
          return false;
        if (result.blocked) {
          toast.error(
            "Start your questionnaire first, then attach your files.",
          );
          return;
        }
        // A refused URL never became an asset, so say so out loud. Silence here
        // is the exact failure this branch exists to kill: a Vimeo link used to
        // land as a screenshot of a player page and the questionnaire was
        // written about an ad nobody read.
        for (const refused of result.refusedUrls) {
          toast.error(refused.reason);
        }
        const capturedMedia = (values.captured ?? []).map(({ file }) => ({
          src: file.publicUrl,
          mimeType: file.mimeType,
        }));
        if (values.files.length > 0 || capturedMedia.length > 0) {
          setCollectFiles((prev) =>
            new Map(prev).set(message.id, {
              files: values.files,
              mediaUrls: capturedMedia,
            }),
          );
        }
        // The durable receipt carries the prepared mutations and identifies
        // the assets answering this card; one that collects is named by the
        // server once applied. Nothing collected: keep the intake. An attach
        // card can also be answered by continuing without a file.
        const collects = Boolean(
          result.assetOperations?.some(
            (operation) => operation.op === "collect_assets",
          ),
        );
        const assetKeys = collects
          ? []
          : receiptAssetKeysOf(result.assetCatalog, message.collect);
        if (
          assetKeys.length === 0 &&
          !collects &&
          !message.collect.attachTo &&
          !message.collect.optional
        )
          return false;
        await sendCommand({
          kind: "collect_receipt",
          assetKeys,
          ...(result.assetOperations?.length
            ? { assetOperations: result.assetOperations }
            : {}),
        });
      } catch (error) {
        console.error("Failed to add collected assets:", error);
        // The Save's mapper: a refused write reads as it does on Save.
        const copy = writeFailureCopy(error, {
          draftId,
          draft: workspace.savedDraft,
          fallback: "Failed to add your files",
        });
        toast.error(copy.title, {
          description: copy.description ?? undefined,
        });
        throw error; // CollectCard stays editable for a retry.
      } finally {
        setAssetWritesInFlight((n) => n - 1);
        setIntakeAnsweringCardId(null);
      }
    },
    [draftId, addCollectedAssets, sendCommand, workspace.savedDraft],
  );

  // Composer asset intake: a file pasted or dropped on the composer, or a
  // message that is nothing but links (`linksOnly`), becomes a tested asset of
  // the family the confirm card's option names. The option is the one action
  // (QA4 AD N6): nothing goes to the chat beside it. A message with words is
  // the user's message and goes to the chat as it is; it never reaches here,
  // so no words wait on this write (a brief that opens "Test a Pepsi video ad
  // ..." is the planner's to read, whole).
  const handleChatAssetAdd = useCallback(
    async (
      assetType: TestedAssetType,
      values: { urls: Array<string>; files: Array<File> },
      /**
       * Nothing was added ("Keep editing", a refused write): the confirm card
       * asks again, with its files and links.
       */
      onNotAdded: () => void,
    ) => {
      const noun = assetFamilyNoun(assetType);
      // Capturing a page screenshot (or uploading images) takes a few seconds with
      // no other on-screen signal, so show a dismissable loading toast meanwhile.
      const toastId = toast.loading(
        values.urls.length > 0
          ? `Capturing the ${noun}…`
          : `Adding the ${noun}…`,
      );
      setAssetWritesInFlight((n) => n + 1);
      try {
        const sendsReceipt = Boolean(studyGoals) &&
          (workspace.currentDraft?.researchQuestions?.length ?? 0) === 0;
        const result = await addCollectedAssets(
          { assetType },
          values,
          sendsReceipt,
        );
        toast.dismiss(toastId);
        if (result.blocked) {
          // Nothing was added: the confirm card asks again. A failed save has
          // already said why; the card only opens once a draft exists.
          onNotAdded();
          return;
        }
        // A video host we cannot read is refused by name, with what to do
        // instead. Attaching a screenshot of the player page would leave the
        // questionnaire to be written about an ad nobody ever watched.
        for (const refused of result.refusedUrls) {
          appendAssistantPrompt({
            content: `${refused.url}: ${refused.reason}`,
          });
        }
        const saidSkips = sayCollectedSkips(result.effects);
        if (result.assetOperations?.length) {
          // Before the questions, once the objective exists: the collect
          // card's own receipt, so intake advances. The turn applies and
          // reads what it carries, names what it added, and says what it
          // skipped as the Save does.
          await sendCommand({
            kind: "collect_receipt",
            assetKeys: [],
            assetOperations: result.assetOperations,
          });
        } else if (result.addedAssetKeys.length === 0) {
          if (
            result.failedCaptures.length === 0 &&
            result.refusedUrls.length === 0 &&
            !saidSkips
          ) {
            appendAssistantPrompt({
              content: `That ${noun} is already attached, so I didn't add it again.`,
            });
          }
        } else if (
          (workspace.currentDraft?.researchQuestions?.length ?? 0) === 0
        ) {
          // With no questions yet and no objective, "update the questions"
          // would be a card whose Yes sends a canned turn the intake chain
          // would consume as the answer to whatever it is asking: re-ask
          // the objective instead. With questions, the one post-commit rule
          // offers the card for what the write added.
          const addedCount = result.addedAssetKeys.length;
          const countLabel =
            addedCount === 1 ? `the ${noun}` : `${addedCount} ${noun}s`;
          appendAssistantPrompt({
            content: `Added ${countLabel}. Now, back to the objective: what decision does this study need to inform?`,
          });
        }
        // A failed capture can't become a usable asset (no screenshot + no
        // description = an incomplete, unsaveable asset). Preserve the typed
        // reason so height/resource failures are not misreported as bot walls.
        for (const content of captureFailurePrompts(
          result.failedCaptures,
          `as ${assetBindingNounWithArticle(assetType)}`,
        )) {
          appendAssistantPrompt({ content });
        }
      } catch (error) {
        toast.dismiss(toastId);
        console.error("Failed to add asset from chat:", error);
        // The Save's mapper: a refused write reads as it does on Save.
        const copy = writeFailureCopy(error, {
          draftId,
          draft: workspace.savedDraft,
          fallback: "Couldn't add that — please try again.",
        });
        toast.error(copy.title, {
          description: copy.description ?? undefined,
        });
        // Nothing was added: the confirm card asks again with its files.
        onNotAdded();
      } finally {
        setAssetWritesInFlight((n) => n - 1);
      }
    },
    [
      addCollectedAssets,
      appendAssistantPrompt,
      sayCollectedSkips,
      sendCommand,
      draftId,
      studyGoals,
      workspace.currentDraft,
      workspace.savedDraft,
      t,
    ],
  );

  // The confirm card's "Add it to <item>" (one file or link) or "Add them to
  // the items without a picture" (several files): each file, or the page a
  // link captures, becomes an item's picture by the attach card's own write
  // (F19, `addCollectedAssets` with `collect.attachTo`), on every price
  // version of a product alike, in the items' order; nothing is added. The
  // write never replaces a file an item got in between. Its receipt says which
  // file went on which item. The items are already in the questions, so no
  // card follows.
  const handleChatAssetAttach = useCallback(
    async (
      targets: ReadonlyArray<MaterialAttachOffer>,
      values: { urls: Array<string>; files: Array<File> },
      /** Nothing was written: the confirm card asks again. */
      onNotAdded: () => void,
    ) => {
      const items = targets.map(({ item }) => item);
      // File by file, the item it goes on: each takes `FILES_PER_ASSET`.
      const given = values.urls.length > 0 ? values.urls : values.files;
      const placed = given.map(
        (_given, index) => items[Math.floor(index / FILES_PER_ASSET)],
      );
      const toastId = toast.loading(
        values.urls.length > 0 ? "Capturing the page…" : "Adding the file…",
      );
      setAssetWritesInFlight((n) => n + 1);
      try {
        const result = await addCollectedAssets(
          { assetType: targets[0].family, attachTo: items },
          { ...values, targets: placed },
        );
        toast.dismiss(toastId);
        if (result.blocked) {
          onNotAdded();
          return;
        }
        for (const refused of result.refusedUrls) {
          appendAssistantPrompt({
            content: `${refused.url}: ${refused.reason}`,
          });
        }
        const attached = result.attachedItems ?? [];
        const receipt = attachReceiptLinesOf(
          result.assetCatalog,
          { attachTo: attached },
          attached.flatMap((item) => item.assetKeys),
        ).map((line) =>
          line.file
            ? t("questionnaire.attachCard.receiptLine", {
                file: line.file,
                item: line.item,
              })
            : t("questionnaire.attachCard.receiptLineNoName", {
                item: line.item,
              }),
        );
        // An item that got its file in between keeps it (the write skipped it).
        const taken =
          result.failedCaptures.length > 0
            ? []
            : items
                .filter((item) => !attached.includes(item))
                .map((item) =>
                  t("questionnaire.materialCard.alreadyHasFile", {
                    item: item.label,
                  }),
                );
        if (receipt.length + taken.length > 0) {
          appendAssistantPrompt({ content: [...receipt, ...taken].join("\n") });
        }
        for (const content of captureFailurePrompts(
          result.failedCaptures,
          `to "${items[0].label}"`,
        )) {
          appendAssistantPrompt({ content });
        }
      } catch (error) {
        toast.dismiss(toastId);
        console.error("Failed to attach a file from chat:", error);
        const copy = writeFailureCopy(error, {
          draftId,
          draft: workspace.savedDraft,
          fallback: "Couldn't add that — please try again.",
        });
        toast.error(copy.title, {
          description: copy.description ?? undefined,
        });
        onNotAdded();
      } finally {
        setAssetWritesInFlight((n) => n - 1);
      }
    },
    [addCollectedAssets, appendAssistantPrompt, draftId, t, workspace.savedDraft],
  );

  /**
   * The asset-confirm card for a file or links handed over: what they can
   * become in this study (`materialOffers`, the registry's typed facts),
   * asked before anything is written, and handled by `tryHandleLocalCard`.
   * One card for a dropped file, a message that is nothing but links, and a
   * link the model handed over from a message with words (a not-done part's
   * typed `link`, whose family is the model's). A family with room for fewer
   * than were given takes that many, and the card says so. When nothing can
   * take them, a line says the limit, or asks what they show, instead.
   * Returns whether a card took them: after a line, they are still the
   * caller's.
   */
  const offerMaterial = useCallback(
    (
      values: { urls: Array<string>; files: Array<File> },
      families: ReadonlyArray<TestedAssetType> | null = studyFamilies,
    ): boolean => {
      const isLink = values.urls.length > 0;
      const count = isLink ? values.urls.length : values.files.length;
      const offers: MaterialOffers = materialOffers(
        isLink ? "link" : "media",
        count,
        { families, assets: workspace.currentDraft?.testedAssets?.assets ?? [] },
        respondentWordingFor(workspace.currentDraft?.contentLanguage),
      );
      const limitLine = (limited: TestedAssetType) =>
        t("questionnaire.typeCapReached", {
          count: TESTED_ASSET_MAX_BY_TYPE[limited],
          type: t(`questionnaire.assetTypes.${limited}.plural`).toLocaleLowerCase(),
        });
      // Each item takes the registry's `filesPerAsset` (F19).
      const capacity = offers.attachTo.length * FILES_PER_ASSET;
      const attachEach =
        count > 1 && count <= capacity
          ? offers.attachTo.slice(0, Math.ceil(count / FILES_PER_ASSET))
          : [];
      const attachOne = count === 1 ? offers.attachTo : [];
      // More than the items take: the card says so and the user picks the
      // one to use; the rest stay out, named (BP-01).
      const given = isLink ? values.urls : values.files.map((file) => file.name);
      const pickFrom =
        offers.attachTo.length > 0 && count > capacity ? given : [];
      if (
        offers.addAs.length === 0 &&
        attachOne.length === 0 &&
        attachEach.length === 0 &&
        pickFrom.length === 0
      ) {
        // Nothing the study uses can take it (B2): never a family the study
        // does not test. Its words make the item, and the model asks for the
        // file on the reply's upload card, whose intake takes the file the
        // caller still holds.
        appendAssistantPrompt({
          content:
            offers.atLimit.length > 0
              ? offers.atLimit.map(limitLine).join(" ")
              : t("questionnaire.materialCard.describeIt"),
        });
        return false;
      }
      const kind = isLink
        ? "link"
        : values.files.every((file) => isSupportedVideoMime(file.type))
          ? "video"
          : values.files.every((file) => file.type.startsWith("image/"))
            ? "image"
            : "file";
      const partial =
        offers.addAs.length === 1 && offers.addAs[0].room < count
          ? offers.addAs[0]
          : null;
      // With one item, a pick names it; with several, the file picked is
      // offered again on its own, and that card asks for its item.
      const pickFor =
        pickFrom.length > 0 && offers.attachTo.length === 1
          ? offers.attachTo[0].item.label
          : null;
      const cardId = appendAssistantPrompt({
        content: [
          t("questionnaire.materialCard.ask", {
            given: t(`questionnaire.materialCard.given.${kind}`, { count }),
          }),
          pickFrom.length === 0
            ? ""
            : pickFor !== null
              ? t("questionnaire.materialCard.pickOne", { item: pickFor })
              : t("questionnaire.materialCard.pickEach"),
          partial
            ? t("questionnaire.materialCard.addsFirst", {
                limit: limitLine(partial.family),
                count: partial.room,
              })
            : "",
        ]
          .filter(Boolean)
          .join(" "),
        options: [
          ...pickFrom.map((name, index) => ({
            label:
              pickFor !== null
                ? t("questionnaire.materialCard.useFor", {
                    file: name,
                    item: pickFor,
                  })
                : t("questionnaire.materialCard.use", { file: name }),
            value: pickGivenOptionValue(index),
          })),
          ...offers.addAs.map(({ family: offered }) => ({
            // Offered only when its registry materials take a file or a link:
            // an ad, a landing page or a pack, each with its label.
            label: t(
              `questionnaire.materialCard.addAs.${offered as "ad" | "landing_page" | "packaging"}`,
            ),
            value: addAsOptionValue(offered),
          })),
          ...attachOne.map(({ item }, index) => ({
            label: t("questionnaire.materialCard.addTo", { item: item.label }),
            value: attachOptionValue(index),
          })),
          ...(attachEach.length > 0
            ? [
                {
                  label: t("questionnaire.materialCard.addToEach"),
                  value: ATTACH_EACH,
                },
              ]
            : []),
          {
            label: t("questionnaire.materialCard.leaveOut"),
            value: ASSET_CONFIRM_CANCEL,
          },
        ],
      });
      setLocalCardActions((current) =>
        new Map(current).set(cardId, {
          kind: "add_asset",
          ...values,
          room: Object.fromEntries(
            offers.addAs.map(({ family: offered, room }) => [offered, room]),
          ),
          // One file, or more than the items take: every item, by the option.
          attachTo: attachEach.length > 0 ? attachEach : offers.attachTo,
        }),
      );
      return true;
    },
    [appendAssistantPrompt, studyFamilies, t, workspace.currentDraft],
  );

  // A reply whose not-done parts hand links over (the model decided the user
  // gave them as material, QA4): the links the user's message holds, for the
  // card, and the parts that stay lines of the reply.
  const handedOver = useMemo(() => {
    const byReply = new Map<string, ReturnType<typeof handedOverLinks>>();
    let userMessage = "";
    for (const message of messages) {
      if (message.role === "user") userMessage = message.content;
      if (message.role === "assistant" && message.notDone) {
        byReply.set(
          message.id,
          handedOverLinks(message.notDone.parts, userMessage),
        );
      }
    }
    return byReply;
  }, [messages]);

  // Its card is offered once, when the reply arrives: a reply already in the
  // transcript when it loads was answered, or not, on an earlier visit.
  const seenReplyIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (loadingDraft) return;
    if (seenReplyIdsRef.current === null) {
      seenReplyIdsRef.current = new Set(messages.map((message) => message.id));
      return;
    }
    const seen = seenReplyIdsRef.current;
    for (const message of messages) {
      if (seen.has(message.id)) continue;
      seen.add(message.id);
      for (const link of handedOver.get(message.id)?.links ?? []) {
        offerMaterial({ urls: [link.url], files: [] }, [link.family]);
      }
    }
  }, [handedOver, loadingDraft, messages, offerMaterial]);

  /**
   * Runs a card the builder added itself (`LocalCardAction`), from the card's
   * own action. Returns true when it ran (so the caller sends nothing to the
   * planner). Honoured only for the card's own options, so typing an option's
   * value into the Other box cannot fire it, and a card answered by typing
   * falls through to an ordinary turn.
   */
  const tryHandleLocalCard = useCallback(
    (
      card: { id: string; options?: Array<{ value: string }> },
      value: string,
      /** Re-opens the card when nothing ran. */
      reopenCard: () => void,
    ): boolean => {
      const action = localCardActions.get(card.id);
      if (!action || !card.options?.some((option) => option.value === value)) {
        return false;
      }
      if (action.kind === "use_new_assets") {
        if (value === NEW_ASSETS_CONFIRM.update) {
          // A turn that did not commit (blocked before it went out, failed or
          // stopped) changed nothing: the card asks again, with its assets.
          void sendGuardedChatTurn({
            command: { kind: "use_new_assets", assetKeys: action.assetKeys },
          }).then((committed) => {
            if (!committed) reopenCard();
          });
        }
        return true;
      }
      if (value === ASSET_CONFIRM_CANCEL) {
        // A link left out is no study material: twins never see the page,
        // and their research reads what the questions name (EX-22, D21).
        appendAssistantPrompt({
          content:
            action.urls.length > 0
              ? t("questionnaire.materialCard.leftOutLink", {
                  count: action.urls.length,
                })
              : t("questionnaire.materialCard.leftOut"),
        });
        return true;
      }
      const family = familyOfAddAsOption(value);
      if (family) {
        const room = action.room[family] ?? 0;
        void handleChatAssetAdd(
          family,
          { urls: action.urls.slice(0, room), files: action.files.slice(0, room) },
          reopenCard,
        );
        return true;
      }
      // The one file or link the user picked, given more than the items take:
      // the rest stay out, named, and the pick goes on the one item, or is
      // offered on its own to choose among several.
      const isLink = action.urls.length > 0;
      const givenNames = isLink
        ? action.urls
        : action.files.map((file) => file.name);
      const picked = givenNames.findIndex(
        (_name, index) => pickGivenOptionValue(index) === value,
      );
      if (picked >= 0) {
        const chosen = isLink
          ? { urls: [action.urls[picked]], files: [] }
          : { urls: [], files: [action.files[picked]] };
        const others = givenNames.filter((_name, index) => index !== picked);
        appendAssistantPrompt({
          content: t("questionnaire.attachCard.leftOut", {
            count: others.length,
            files: others.join(", "),
          }),
        });
        if (action.attachTo.length === 1) {
          void handleChatAssetAttach(action.attachTo, chosen, reopenCard);
        } else {
          offerMaterial(chosen);
        }
        return true;
      }
      const targets =
        value === ATTACH_EACH
          ? action.attachTo
          : action.attachTo.filter(
              (_offer, index) => attachOptionValue(index) === value,
            );
      if (targets.length > 0) {
        void handleChatAssetAttach(targets, action, reopenCard);
      }
      return true;
    },
    [
      appendAssistantPrompt,
      handleChatAssetAdd,
      handleChatAssetAttach,
      localCardActions,
      offerMaterial,
      sendGuardedChatTurn,
      t,
    ],
  );

  useAutosizeTextarea(textareaRef, input, {
    maxHeightPx: CHAT_TEXTAREA_MAX_HEIGHT_PX,
    disabled: chatInputDisabled,
  });

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // The slow warning grows the transcript inside a single send, while
    // `messages` and `sending` hold still — without it here the new row lands
    // below the fold.
  }, [messages, sending, showSlowWarning]);

  // The composer takes focus back when a lock ends only if it had focus as
  // the lock began (B22): a lock never pulls focus from where the user is.
  const composerFocusedRef = useRef(false);
  useEffect(() => {
    if (chatInputDisabled) return;
    if (composerFocusedRef.current) textareaRef.current?.focus();
  }, [chatInputDisabled]);

  const displaySurveyJson = workspace.currentSurveyJson;
  const surveyQuestionCount = workspace.questionCount;
  // The backend owns review eligibility: the minimum question count depends on
  // the ad-instrument roster, so a legal four-question ad study is review-ready
  // and a hardcoded 8 here would lock it out.
  //
  // Unsaved edits keep the button live rather than making the user guess why it
  // greyed out — the verdict in hand describes the last SAVED draft, so it
  // cannot speak for what is on screen. handleAdvance saves first and refuses
  // on the fresh verdict, so an optimistic click costs a round trip, never a
  // bad review.
  const reviewEligible =
    workspace.isDirty || readiness?.isReviewEligible === true;
  // The banner's filter (one owner): an empty shell's count and missing
  // brief are not something to fix, so its line never says a question does.
  const firstBlockingIssue = shownAuthoringIssues(
    shownBlockingIssues,
    workspace.savedDraft,
  )[0];
  const reviewBlockedReason = firstBlockingIssue
    ? formatIssueCopy(firstBlockingIssue, issueCopyContext).text
    : "Review unlocks once the questionnaire is complete.";

  useEffect(() => {
    if (!hasUserSentMessage) {
      // Only count messages the user typed; card-selection echoes are suppressed
      // from the UI and should not trigger the compact input layout.
      // A card's answer is not typed: its record names the message.
      const hasTypedMessage = messages.some(
        (m) => m.role === "user" && !answeredByMessage.has(m.id),
      );
      if (hasTypedMessage) {
        setHasUserSentMessage(true);
      }
    }
  }, [hasUserSentMessage, messages, answeredByMessage]);

  const handleSend = useCallback(async () => {
    if (assetWritesPending || composerLocked) return;
    // Files staged with no words: the material card asks what they become
    // in this study (`offerMaterial`) and takes them. When nothing the study
    // tests can take them, its line asks what they show, and they stay
    // staged for the upload card the reply opens. Words go to the chat as
    // they are (below), and staged files go with them: a turn carries no
    // file, so they are handed on when the words' turn ends
    // (`composerTurnEnded`).
    if (!input.trim() && composerFiles.length > 0) {
      if (offerMaterial({ urls: [], files: composerFiles })) {
        setComposerFiles([]);
      }
      return;
    }
    // A message is the user's message and goes to the chat as it is, links and
    // all: the model reads what its words ask for, and the builder never reads
    // them (F15: the detectAssetIntent word regexes are deleted). A message
    // that is nothing but links (`linksOnly`, the URL format) hands the links
    // over as material, like a dropped file: once a draft exists to attach to
    // (a fresh study has no draftId yet, so the planner makes the first
    // draft), they move onto the asset-confirm card, whose option is the one
    // action. Before QA4 (MS N2, AD N6, PN N6) every message with a link waited
    // hidden in the composer for that card, then went out beside its action,
    // and the model decided a second time what the link was for. On a card
    // that collects pasted material the words, links included, are that card's
    // answer. The links leave the composer only when the card takes them:
    // when nothing the study tests can, its line asks what they are, and they
    // stay to be sent with the words that say it, as staged files do.
    const urls = draftId && !openCardCollectsText ? linksOnly(input) : [];
    if (urls.length > 0) {
      if (offerMaterial({ urls, files: [] })) setInput("");
      return;
    }
    // Persist on-screen edits first (the chat edits the PERSISTED draft); if the
    // save fails, surface it and keep the input rather than silently dropping it.
    // Text-collect cards must be valid and saved before a turn that can PLAN.
    const carriesFiles = composerFiles.length > 0;
    void sendGuardedChatTurn(input, { from: "composer" }).then(() => {
      if (carriesFiles) setComposerTurnEnded(true);
    });
  }, [
    assetWritesPending,
    composerFiles,
    composerLocked,
    draftId,
    input,
    offerMaterial,
    openCardCollectsText,
    sendGuardedChatTurn,
    setInput,
  ]);

  // Images and videos attached, pasted or dropped on the composer are
  // candidate tested assets, but we ASK before creating (a dropped file could
  // be reference material, not a stimulus). They are staged beside the
  // composer's words, which stay so the user can say what the file is
  // (MT-01), and go where the send takes them (`handleSend`). Returns true
  // when files were handled (caller suppresses the default text paste/drop).
  const handleComposerMediaFiles = useCallback(
    (files: Array<File>): boolean => {
      const gate = gateComposerMediaFiles(files);
      if (gate.kind === "ignore") return false;
      if (gate.kind === "unsupported") {
        // Files were given but none are usable: say so instead of silently
        // swallowing them (the drop already suppressed the default handling).
        toast.error(
          "That file type isn't supported. Attach images or MP4, WebM, or MOV videos.",
        );
        return true;
      }
      if (!draftId) {
        toast.error("Start your questionnaire first, then attach images.");
        return true;
      }
      setComposerFiles((current) => [...current, ...gate.media]);
      return true;
    },
    [draftId],
  );

  const handleComposerPaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (handleComposerMediaFiles(files)) {
        event.preventDefault();
      }
    },
    [handleComposerMediaFiles],
  );

  const handleComposerDrop = useCallback(
    (event: React.DragEvent<HTMLTextAreaElement>) => {
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0) return;
      event.preventDefault();
      handleComposerMediaFiles(files);
    },
    [handleComposerMediaFiles],
  );

  // The explore confirm card's "Build manually" / "Paste questionnaire"
  // options: the one road that opens an empty workspace. Errors are toasted
  // inside the hook. The opening is in flight until its request settles (the
  // workspace itself shows at once), and the chat's controls stay locked on
  // it: a second click would write over the first, and its failure would put
  // back the pre-click draft.
  const tryHandleExploreConfirm = useCallback(
    async (
      value: string,
      card: { id: string; options: Array<ChatMessageOption> },
    ) => {
      const action = resolveExploreConfirmAction(
        value,
        card.options.map((option) => option.value),
      );
      if (!action) {
        return false;
      }
      setIsOpeningWorkspace(true);
      try {
        // The card is answered in the PATCH that opens the workspace (S4.1).
        await startManualDraft({
          intent: action,
          answers: interceptAnswer(card, value),
        });
        if (action === "paste") {
          setInvitePaste(true);
          textareaRef.current?.focus();
        }
      } catch {
        // Said by the hook.
      } finally {
        setIsOpeningWorkspace(false);
      }
      return true;
    },
    [interceptAnswer, startManualDraft],
  );

  const handleAdvance = useCallback(async () => {
    if (didAdvance || assetWritesPending) {
      return;
    }

    // Review compiles the saved draft, so every local buffer is saved first,
    // once the user agrees. The Save's commit compiles in the same request, so
    // its result is the verdict and survey Review acts on.
    let saved: Awaited<ReturnType<typeof saveEdits>> | undefined;
    if (!(await saveEditsIfConfirmed((result) => {
      saved = result;
    }))) {
      return;
    }

    setIsAdvancing(true);

    try {
      // Every Review click compiles the draft the server currently holds, and
      // advances with THAT survey. The cached `workspace.currentSurveyJson` is
      // never the payload and never a reason to skip the compile: a compiled
      // survey does not say which draft produced it, so a reload after a save,
      // or a save whose compile failed, leaves one that silently describes an
      // older questionnaire. Compilation is deterministic and local, so the
      // honest answer is cheaper than tracking which draft the cache belongs to.
      //
      // Saving first is what makes "the persisted draft" the draft on screen;
      // when there is nothing to save, that is already true and we compile alone.
      // Either way the second click after a failure reaches the compiler again,
      // with no unrelated edit or reload needed.
      const compiled = saved?.draft
        ? {
            surveyJson: saved.surveyJson,
            readiness: saved.readiness ?? null,
            revision: saved.revision ?? null,
            audience: saved.draft.audience ?? null,
          }
        : await compilePersistedDraft();

      // The fresh backend verdict is the only advance authority. Checked BEFORE
      // the survey is: on a draft the backend blocks, the save legitimately
      // returns no survey, and bailing on that first would swallow the blocking
      // issue and leave the click looking like it did nothing.
      const advanceReadiness = compiled.readiness;
      if (!advanceReadiness?.isReviewEligible) {
        const firstIssue = advanceReadiness?.blockingIssues?.[0];
        toast.error(
          firstIssue
            ? formatIssueCopy(firstIssue, {
                resolvedMinimum: advanceReadiness?.resolvedMinimum,
                draftId,
              }).text
            : t("questionnaire.reviewRetry"),
        );
        return;
      }

      if (!compiled.surveyJson || compiled.revision === null) {
        toast.error(t("questionnaire.reviewRetry"));
        return;
      }

      const title = workspace.title;

      const success = await onSurveyReady(
        compiled.surveyJson,
        compiled.revision,
        title,
        compiled.audience,
      );
      if (success) {
        setDidAdvance(true);
      }
    } finally {
      setIsAdvancing(false);
    }
  }, [
    assetWritesPending,
    compilePersistedDraft,
    didAdvance,
    draftId,
    onSurveyReady,
    saveEditsIfConfirmed,
    workspace,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Turn-zero receipt placement (Wave 7, Step 2): OPENING_CHAINS[goalId][0]
  // is always an objective-only `_decision` card, seeded as the transcript's
  // very first message before any user reply exists: the backend contract
  // this whole plan enforces. Nothing that survives to the client per-message
  // (kind/content) marks that card specifically, so chatState carries no
  // anchor for it; the receipt instead anchors on the first user-role message
  // in the WHOLE transcript, which is necessarily the reply to it, whatever
  // form that reply takes (typed text or a DecisionCard button echo).
  const firstUserMessageIndex = useMemo(
    () =>
      // The transcript's own: an attempt that failed before it stays in the
      // chat (`isLocalMessage`), but set no objective.
      messages.findIndex(
        (message) => message.role === "user" && !isLocalMessage(message),
      ),
    [messages],
  );

  // The card waiting on an answer: the open record's, or a local prompt.
  const lastUnansweredComposerMsg = findLastUnansweredComposerMessage(
    messages,
    isWaitingCard,
  );
  const activeDecisionMsg =
    !sending &&
    lastUnansweredComposerMsg &&
    isUnansweredDecision(lastUnansweredComposerMsg, isWaitingCard)
      ? lastUnansweredComposerMsg
      : null;
  const activeCollectMsg =
    !sending &&
    lastUnansweredComposerMsg &&
    isUnansweredCollect(lastUnansweredComposerMsg, isWaitingCard)
      ? lastUnansweredComposerMsg
      : null;
  // An upload card opens with the files staged in the composer: its intake
  // takes them as it mounts (`initialFiles`), so they leave the composer.
  const activeCollectId = activeCollectMsg?.id ?? null;
  useEffect(() => {
    if (activeCollectId) {
      setComposerFiles((current) => (current.length > 0 ? [] : current));
    }
  }, [activeCollectId]);
  // Files sent with the composer's words go where the words went, once their
  // turn has ended (L25: they stayed staged when no card took them). Words
  // back in the composer (stopped, never sent, or a failure that hands them
  // back to reword) keep their files beside them. Words the turn took
  // (committed, or held by its Retry) take the files on: to the reply's
  // upload card when it opens one (above), else to the material card, which
  // asks what they become, as for files sent with no words.
  useEffect(() => {
    if (!composerTurnEnded) return;
    setComposerTurnEnded(false);
    if (input.trim() || activeCollectId || composerFiles.length === 0) return;
    if (offerMaterial({ urls: [], files: composerFiles })) setComposerFiles([]);
  }, [activeCollectId, composerFiles, composerTurnEnded, input, offerMaterial]);

  // Persisted asset media — the reload fallback for the sent-attachments bubble
  // once the in-memory Files are gone. The mime rides along because a hosted
  // https URL alone can't tell the bubble it is a video.
  const collectAssetMedia = useMemo(
    () =>
      workspace.conceptAssets.flatMap((asset) => {
        const media = asset.media?.find(
          (m) => m.kind === "image" || m.kind === "video",
        );
        const src = media?.publicUrl || media?.url || media?.filePath || "";
        if (!media || !src) return [];
        // kind is the authoritative signal when a persisted ref lost its mime.
        const mimeType =
          media.mimeType ?? (media.kind === "video" ? "video/mp4" : undefined);
        // The tile is named by its asset, never the generic "Creative".
        return [{ src, mimeType, label: asset.name }];
      }),
    [workspace.conceptAssets],
  );
  // Only the most recent collect turn uses the persisted-asset fallback, so the
  // same images aren't repeated across multiple collect receipts after a reload.
  const lastCollectMsgId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "assistant" && messages[i].collect) {
        return messages[i].id;
      }
    }
    return null;
  }, [messages]);

  // The upload card of a family made from a link (ads, pages) starts with
  // the links the user's message before it holds, read by the one URL parser;
  // the user's "Add to study" is the decision, so nothing is captured before
  // it (lead decision on the QA4 review, option 2: the pre-draft brief that
  // named its ad by link).
  const briefLinksForCard = useMemo(() => {
    if (!activeCollectMsg || activeCollectMsg.collect.attachTo) return [];
    if (
      !isAssetFamily(activeCollectMsg.collect.assetType) ||
      !LINK_MATERIAL_FAMILIES.includes(activeCollectMsg.collect.assetType)
    ) {
      return [];
    }
    const index = messages.findIndex(
      (message) => message.id === activeCollectMsg.id,
    );
    const brief = messages
      .slice(0, index)
      .reverse()
      .find((message) => message.role === "user");
    return brief ? messageLinks(brief.content) : [];
  }, [activeCollectMsg, messages]);

  // What the waiting collect card has collected while its record is open:
  // the saved assets of its type (plan S4.1). A receipt turn that failed or
  // was stopped left them saved, so the intake offers the receipt for them
  // instead of asking for them again.
  const collectedByActiveCard = activeCollectMsg
    ? receiptAssetKeysOf(
        workspace.savedDraft?.testedAssets,
        activeCollectMsg.collect,
      )
    : [];

  /**
   * The builder's Save button. The collect card and the assets panel share
   * one workspace, so assets added in the panel are the card's too: when the
   * user's own Save saves new assets of the type the waiting card asks for,
   * it answers the card with every asset the card has collected. Only this
   * Save sends that receipt; a page load, an unsaved add or a declined
   * "Save and continue" sends nothing. Like every Save, it offers the
   * "update the questions?" card by its one rule (`saveEdits`).
   */
  const saveFromPanel = useCallback(async () => {
    const card = activeCollectMsg;
    const savedAssetsBefore = workspace.savedDraft?.testedAssets;
    let saved: Awaited<ReturnType<typeof saveEdits>> | undefined;
    if (!(await savePendingBuffers((result) => {
      saved = result;
    }))) return;
    if (!saved?.draft || !card) return;
    const before = receiptAssetKeysOf(savedAssetsBefore, card.collect);
    const assetKeys = receiptAssetKeysOf(saved.draft.testedAssets, card.collect);
    if (assetKeys.every((key) => before.includes(key))) return;
    await sendCommand({ kind: "collect_receipt", assetKeys });
  }, [activeCollectMsg, saveEdits, savePendingBuffers, sendCommand, workspace.savedDraft]);

  const handleDecisionSelect = useCallback(
    async (value: string) => {
      if (!activeDecisionMsg || chatInputDisabled) return;
      if (isLocalMessage(activeDecisionMsg)) {
        setLocalCardAnswers((prev) =>
          new Map(prev).set(activeDecisionMsg.id, value),
        );
      }
      setInputOtherExpanded(false);
      setInputOtherText("");
      const reopenCard = () => {
        setLocalCardAnswers((prev) => {
          const reverted = new Map(prev);
          reverted.delete(activeDecisionMsg.id);
          return reverted;
        });
      };
      // A card the builder added itself runs locally — never a planner turn.
      if (tryHandleLocalCard(activeDecisionMsg, value, reopenCard)) {
        return;
      }
      // The topic gate's "No" opens the audience picker rather than drafting
      // against an audience that cannot answer the study.
      const decision = resolveDecisionCardAction(
        value,
        (activeDecisionMsg.options ?? []).map((option) => option.value),
      );
      if (decision.kind === "change_audience") {
        // The gate is answered by the audience write it leads to (S4.1).
        const answers = interceptAnswer(activeDecisionMsg, value);
        if (answers) answerWithNextAudienceChange(answers);
        void editAudience();
        return;
      }
      if (await tryHandleExploreConfirm(value, activeDecisionMsg)) {
        return;
      }
      const optionId = (activeDecisionMsg.options ?? []).find(
        (option) => option.value === value,
      )?.id;
      // Persist unsaved builder edits first so the planner sees the user's latest
      // questions (the server reconcile is governed by the persisted draft, so an
      // unsaved client-added question would otherwise be dropped). Text-collect
      // cards (claims / pains / pricing / concept) must also be valid before a
      // DecisionCard turn that can reach PLAN. A chosen option sends its id
      // with the text (plan S3.3, L16).
      void sendGuardedChatTurn(value, {
        optionId,
        onBlocked: reopenCard,
      });
    },
    [
      activeDecisionMsg,
      answerWithNextAudienceChange,
      chatInputDisabled,
      interceptAnswer,
      onEditAudience,
      sendGuardedChatTurn,
      tryHandleLocalCard,
      tryHandleExploreConfirm,
    ],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {headerSlot ? (
        <div className="flex flex-shrink-0 items-center justify-end gap-2 pb-3">
          {headerSlot}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 gap-4">
        <div className="flex w-full min-w-0 flex-col lg:w-1/3">
          {/* lang attribute improves browser glyph selection and screen-reader
              behaviour when the study language uses a non-Latin script (e.g. ja). */}
          {/* pt/pb keep the active card's glow ring from being clipped by
              the scroll container's overflow at the first/last message. */}
          <div
            ref={scrollRef}
            role="log"
            aria-live="polite"
            lang={language || undefined}
            className="flex-1 min-h-0 space-y-5 overflow-y-auto pb-3 pr-2 pt-3 scroll-smooth"
          >
            {messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;
              // Everything below is the ORIGINAL per-message render logic,
              // unchanged, just wrapped so the receipt can be appended after
              // whichever node it produces (see firstUserMessageIndex above).
              const node = (() => {
                // Assistant messages with a collect surface render as a
                // CollectCard: its question in the transcript, its intake in
                // the composer (`ComposerCollectIntake`) — checked before
                // options since collect cards never carry options.
                if (message.role === "assistant" && message.collect) {
                  return (
                    <CollectCard
                      key={message.id}
                      message={{ ...message, collect: message.collect }}
                      chainCode={chainCodes.get(message.id)}
                      assetPanelHeading={assetPanelHeading}
                      aiIconSrc={aiIconSrc}
                    />
                  );
                }

                // Assistant messages with options render as a DecisionCard:
                // its question in the transcript. The waiting card's options
                // live in the composer (`handleDecisionSelect`).
                if (
                  message.role === "assistant" &&
                  Array.isArray(message.options) &&
                  message.options.length > 0
                ) {
                  const cardMessage = message as typeof message & {
                    options: NonNullable<typeof message.options>;
                  };
                  return (
                    <DecisionCard
                      key={message.id}
                      message={cardMessage}
                      chainCode={chainCodes.get(message.id)}
                      aiIconSrc={aiIconSrc}
                      isActive={isLastMessage && !sending}
                      answered={answeredCardIds.has(message.id)}
                    />
                  );
                }

                // A DecisionCard pick renders as a normal user bubble (like the user
                // answered in chat); a CollectCard receipt renders its attachments as
                // a user bubble. Everything else falls through to the plain bubble.
                if (message.role === "user") {
                  // Prefer the option label over an opaque stored value: the
                  // record names the option (S4.1); a transcript stored before
                  // the records is looked up by the value it stored.
                  const answeredCard = answeredByMessage.get(message.id);
                  const decisionLabel = answeredCard?.option
                    ? builderOptionLabel(
                        answeredCard.option,
                        chainCodes.get(answeredCard.cardId),
                        t,
                      )
                    : resolveDecisionOptionLabel(messages, index, message.content);
                  if (decisionLabel !== null) {
                    return (
                      <ChatBubble
                        key={message.id}
                        message={{
                          ...message,
                          content: decisionLabel,
                        }}
                        isFirst={index === 0}
                        isLast={isLastMessage}
                        userInitials={userInitials}
                        aiIconSrc={aiIconSrc}
                        assetPanelHeading={assetPanelHeading}
                        whiteLabelEnabled={whiteLabelEnabled}
                      />
                    );
                  }
                  const answeredCollect = answeredByMessage.get(message.id);
                  // A receipt that adds nothing (an attach card continued
                  // without a file, F19) has no files to show: its own words
                  // ("None added") show below. One that collects adds what
                  // the user submitted, from the send: the server names its
                  // assets only at commit.
                  const receiptNamesNothing =
                    message.command?.kind === "collect_receipt" &&
                    receiptItemCount(message.command) === 0;
                  // An attach card's receipt says which item got which file
                  // (F19), read off the saved items.
                  const attachCard = answeredCollect?.collect
                    ? messages.find(
                        (candidate) => candidate.id === answeredCollect.cardId,
                      )?.collect
                    : undefined;
                  const attachLines =
                    attachCard?.attachTo &&
                    message.command?.kind === "collect_receipt"
                      ? attachReceiptLinesOf(
                          workspace.savedDraft?.testedAssets,
                          attachCard,
                          message.command.assetKeys,
                        )
                      : [];
                  if (attachLines.length > 0) {
                    return (
                      <ChatBubble
                        key={message.id}
                        message={{
                          ...message,
                          content: attachLines
                            .map((line) =>
                              line.file
                                ? t("questionnaire.attachCard.receiptLine", {
                                    file: line.file,
                                    item: line.item,
                                  })
                                : t("questionnaire.attachCard.receiptLineNoName", {
                                    item: line.item,
                                  }),
                            )
                            .join("\n"),
                        }}
                        isFirst={index === 0}
                        isLast={isLastMessage}
                        userInitials={userInitials}
                        aiIconSrc={aiIconSrc}
                        assetPanelHeading={assetPanelHeading}
                        whiteLabelEnabled={whiteLabelEnabled}
                      />
                    );
                  }
                  if (answeredCollect?.collect && !receiptNamesNothing) {
                    // Instead of the suppressed receipt text, show the images the
                    // user attached as a normal user chat bubble.
                    const sent = collectFiles.get(answeredCollect.cardId);
                    if (sent) {
                      return (
                        <SentAttachmentsBubble
                          key={message.id}
                          files={sent.files}
                          mediaUrls={sent.mediaUrls}
                          userInitials={userInitials}
                          isLast={isLastMessage}
                        />
                      );
                    }
                    // After a reload the Files are gone — fall back to the persisted
                    // asset media (only for the latest collect turn).
                    if (
                      answeredCollect.cardId === lastCollectMsgId &&
                      collectAssetMedia.length > 0
                    ) {
                      return (
                        <SentAttachmentsBubble
                          key={message.id}
                          mediaUrls={collectAssetMedia}
                          userInitials={userInitials}
                          isLast={isLastMessage}
                        />
                      );
                    }
                    // Nothing to show yet (a link, read at commit): the
                    // receipt's own words ("1 added") show below.
                  }
                }

                // A link a not-done part handed over is the card's, not a
                // line of the reply.
                const handed = handedOver.get(message.id);
                return (
                  <ChatBubble
                    key={message.id}
                    message={
                      handed && message.notDone
                        ? {
                            ...message,
                            notDone: { ...message.notDone, parts: handed.lines },
                          }
                        : message
                    }
                    chainCode={chainCodes.get(message.id)}
                    isFirst={index === 0}
                    isLast={isLastMessage}
                    userInitials={userInitials}
                    aiIconSrc={aiIconSrc}
                    assetPanelHeading={assetPanelHeading}
                    whiteLabelEnabled={whiteLabelEnabled}
                  />
                );
              })();

              // The objective a first message set, and what the message's
              // material did: a receipt turn's collect skips (its stored
              // effects) and a paste's committed receipt, so both show after
              // a reload too.
              const skipLines = userMessageReceiptLines(
                {
                  effects:
                    message.command?.kind === "collect_receipt"
                      ? message.effects
                      : undefined,
                  materialReceipt: message.materialReceipt,
                },
                t,
              );
              const objective =
                index === firstUserMessageIndex && studyGoals?.decision
                  ? studyGoals
                  : null;
              if (!objective && skipLines.length === 0) return node;
              return (
                <Fragment key={message.id}>
                  {node}
                  {objective ? (
                    <ObjectiveReceipt
                      decision={objective.decision}
                      source={objective.source}
                    />
                  ) : null}
                  {skipLines.map((content, lineIndex) => (
                    <ChatBubble
                      key={`${message.id}-skip-${lineIndex}`}
                      message={{
                        id: `${message.id}-skip-${lineIndex}`,
                        role: "assistant",
                        content,
                      }}
                      isFirst={false}
                      isLast={false}
                      userInitials={userInitials}
                      aiIconSrc={aiIconSrc}
                      assetPanelHeading={assetPanelHeading}
                      whiteLabelEnabled={whiteLabelEnabled}
                    />
                  ))}
                </Fragment>
              );
            })}

            {sessionError && canRetryLast ? (
              <div className="ml-11 animate-fade-in">
                <button
                  data-testid="chat-retry-last-message"
                  onClick={() => void retryLastAfterEdits()}
                  className="text-xs text-primary underline hover:text-primary/80"
                >
                  {t("questionnaire.retryLast")}
                </button>
              </div>
            ) : null}

            {sending ? (
              <>
                <div
                  className="flex items-center gap-3 pl-[36px] animate-fade-in"
                  role="status"
                  aria-live="polite"
                  data-testid="chat-preparing-indicator"
                >
                  <TypingDots />
                  {sendUnconfirmed ? (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="chat-send-unconfirmed"
                    >
                      {t("questionnaire.turn.sendUnconfirmed")}
                    </p>
                  ) : buildProgressLine ? (
                    <p
                      className="text-xs text-muted-foreground"
                      data-testid="chat-build-progress"
                    >
                      {buildProgressLine}
                    </p>
                  ) : null}
                </div>
                {showSlowWarning ? (
                  <div className="mt-1 pl-[36px] flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">
                      {t("questionnaire.takingLonger")}
                    </p>
                    <button
                      onClick={cancelRequest}
                      disabled={!canStop}
                      className="text-xs text-destructive underline hover:text-destructive/80 disabled:opacity-50 dark:text-red-400 dark:hover:text-red-400/80"
                    >
                      {t("questionnaire.cancel")}
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            {/* A chat-side write that carries a video runs outside a turn, so
                `sending` is still false while the Save watches it. */}
            {readingCreative ? (
              <div
                className="flex items-center gap-3 pl-[36px] animate-fade-in"
                role="status"
                aria-live="polite"
                data-testid="chat-reading-creative-indicator"
              >
                <TypingDots />
                <Text size="sm" className="text-muted-foreground">
                  {t("questionnaire.readingAd")}
                </Text>
              </div>
            ) : null}
          </div>

          <div className="flex-shrink-0 pb-2 pt-3">
            <div
              className={cn(
                "relative flex flex-col rounded-[16px] bg-background py-4 pl-5 pr-[92px] shadow-[0px_6px_17.4px_rgba(0,0,0,0.06)] backdrop-blur-[3.15px] dark:bg-twin-chat-surface dark:shadow-[0px_15px_30px_rgba(0,0,0,0.2)]",
                activeDecisionMsg || activeCollectMsg
                  ? "pr-5"
                  : !hasUserSentMessage
                    ? "min-h-[160px]"
                    : "min-h-[80px]",
              )}
            >
              {activeDecisionMsg ? (
                /* Option buttons replace the textarea when a decision is pending */
                <div className="flex flex-col gap-1.5">
                  {activeDecisionMsg.options.map((option, index) => (
                    <button
                      key={option.value}
                      type="button"
                      data-testid={`chat-decision-option-${index}`}
                      onClick={() => handleDecisionSelect(option.value)}
                      disabled={chatInputDisabled}
                      className={cn(
                        "flex items-center gap-2.5 w-full rounded-lg px-3 py-2 text-sm text-left transition-colors",
                        "border border-border bg-background/60",
                        "hover:bg-accent hover:border-border",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                    >
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {builderOptionMark(index)}
                      </span>
                      <span className="flex-1 text-foreground">
                        {builderOptionLabel(
                          option,
                          chainCodes.get(activeDecisionMsg.id),
                          t,
                        )}
                      </span>
                    </button>
                  ))}

                  {!inputOtherExpanded ? (
                    <button
                      type="button"
                      data-testid="chat-decision-other-toggle"
                      onClick={() => setInputOtherExpanded(true)}
                      disabled={composerLocked}
                      className={cn(
                        "flex items-center gap-2.5 w-full rounded-lg px-3 py-2 text-sm text-left transition-colors",
                        "border border-border/50 bg-background/40 text-muted-foreground",
                        "hover:bg-accent hover:text-foreground hover:border-border",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                    >
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {builderOptionMark(activeDecisionMsg.options.length)}
                      </span>
                      <span className="italic">
                        {/* Locked free text says what it waits on, as the
                            composer does: an open asset card asks for one of
                            its options first. */}
                        {composerLockCause
                          ? lockReasonFor(composerLockCause)
                          : t("questionnaire.otherTypeAnswer")}
                      </span>
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2 rounded-lg border border-border bg-background/60 p-2">
                      <Textarea
                        autoFocus
                        data-testid="chat-decision-other-field"
                        value={inputOtherText}
                        onChange={(e) => setInputOtherText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            const trimmed = inputOtherText.trim();
                            if (trimmed) handleDecisionSelect(trimmed);
                          }
                        }}
                        placeholder={
                          composerLockCause
                            ? lockReasonFor(composerLockCause)
                            : t("questionnaire.typeAnswer")
                        }
                        disabled={composerLocked}
                        rows={2}
                        className="min-h-0 resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setInputOtherExpanded(false);
                            setInputOtherText("");
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          {t("questionnaire.cancel")}
                        </button>
                        <Button
                          type="button"
                          size="sm"
                          variant="cta"
                          data-testid="chat-decision-other-submit"
                          disabled={!inputOtherText.trim() || composerLocked}
                          onClick={() => {
                            const trimmed = inputOtherText.trim();
                            if (trimmed) handleDecisionSelect(trimmed);
                          }}
                          className="h-7 rounded-md px-3 text-xs"
                        >
                          {t("questionnaire.send")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : activeCollectMsg ? (
                /* Asset intake (upload / landing-page URL) lives in the composer,
                   ChatGPT-style — the matching bubble collapses to its prompt. */
                <ComposerCollectIntake
                  key={activeCollectMsg.id}
                  collect={activeCollectMsg.collect}
                  initialFiles={composerFiles}
                  initialLinks={briefLinksForCard}
                  collectedCount={
                    activeCollectMsg.collect.attachTo
                      ? attachItemsWithFileOf(
                          workspace.savedDraft?.testedAssets,
                          activeCollectMsg.collect,
                        )
                      : collectedByActiveCard.length
                  }
                  attachItems={
                    activeCollectMsg.collect.attachTo
                      ? openAttachItemsOf(
                          workspace.savedDraft?.testedAssets,
                          activeCollectMsg.collect,
                        )
                      : undefined
                  }
                  disabled={chatInputDisabled}
                  onCollect={(values) =>
                    handleCollectSubmit(
                      {
                        id: activeCollectMsg.id,
                        collect: activeCollectMsg.collect,
                      },
                      values,
                    )
                  }
                  onStagedChange={setIntakeStaged}
                />
              ) : (
                <>
                  {composerFileTiles.length > 0 ? (
                    <div className="mb-2" data-testid="chat-composer-files">
                      <AttachmentTiles
                        items={composerFileTiles}
                        testIdPrefix="chat-composer-file"
                        align="start"
                        onRemoveAtIndex={
                          composerLocked
                            ? undefined
                            : (index) =>
                                setComposerFiles((current) =>
                                  current.filter((_, i) => i !== index),
                                )
                        }
                      />
                    </div>
                  ) : null}
                  <Textarea
                    ref={textareaRef}
                    onFocus={() => {
                      composerFocusedRef.current = true;
                    }}
                    onBlur={(event) => {
                      // Disabling the composer for a lock is not the user
                      // leaving it.
                      if (!event.currentTarget.disabled) {
                        composerFocusedRef.current = false;
                      }
                    }}
                    placeholder={
                      // The composer is locked on the one busy fact or an open
                      // asset card: it says what it waits on, by its cause, in
                      // the line the panel's locked controls say (one owner,
                      // `lockReasonFor`; a Save says "Saving…", never that the
                      // AI is working), rather than inviting a message it
                      // cannot take. Like the rest of the builder, it is in
                      // the interface's language, whatever language the
                      // questionnaire is in (QA4: a Spanish study turned it
                      // Spanish inside an English builder).
                      composerLockCause
                        ? lockReasonFor(composerLockCause)
                        : invitePaste
                          ? t("questionnaire.pasteQuestions")
                          : clarificationOpen
                            ? t("questionnaire.composerFollowUp")
                            : isDraftReady
                              ? t("questionnaire.composerDraftReady")
                              : t("questionnaire.composerFirstTurn")
                    }
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onPaste={handleComposerPaste}
                    onDrop={handleComposerDrop}
                    onDragOver={(event) => {
                      // Only claim the drag when it carries files, so text DnD is
                      // unaffected.
                      if (
                        Array.from(event.dataTransfer?.types ?? []).includes(
                          "Files",
                        )
                      ) {
                        event.preventDefault();
                      }
                    }}
                    className={cn(
                      // shrink-0, not flex-1: this shell is a column flex with
                      // only a min-height, and flex-1's 0% basis keeps the
                      // field at that leftover height. The autosize height
                      // never applies, so a long message is clipped.
                      "w-full shrink-0 resize-none border-0 bg-transparent p-0 !text-base leading-[1.6] text-twin-chat-fg placeholder:text-twin-chat-fg-muted/50 shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                      !hasUserSentMessage
                        ? "max-h-[200px] min-h-[80px]"
                        : "max-h-[120px] min-h-[24px]",
                    )}
                    rows={!hasUserSentMessage ? 4 : 1}
                    disabled={composerLocked}
                    data-testid="chat-message-input"
                  />
                  {sending ? (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={cancelRequest}
                            // Stop acts on the current turn's id only: off
                            // until the server names it, and once its stop
                            // is asked for.
                            disabled={!canStop}
                            data-testid="chat-stop-button"
                            className="group absolute bottom-[10px] right-[10px] flex size-[32px] items-center justify-center rounded-[8px] bg-cta text-cta-foreground transition-opacity disabled:cursor-default disabled:opacity-60"
                            aria-label={t("questionnaire.stop")}
                          >
                            <Loader2
                              className="h-4 w-4 animate-spin group-hover:hidden group-focus-visible:hidden"
                              aria-hidden="true"
                            />
                            <Square
                              className="hidden h-3 w-3 fill-current stroke-none group-hover:block group-focus-visible:block"
                              aria-hidden="true"
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {t("questionnaire.stop")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={
                        (!input.trim() && composerFiles.length === 0) ||
                        composerLocked
                      }
                      data-testid="chat-send-button"
                      className="absolute bottom-[10px] right-[10px] flex size-[32px] items-center justify-center rounded-[8px] bg-cta text-cta-foreground transition-opacity disabled:opacity-40"
                      aria-label={t("questionnaire.send")}
                    >
                      {chatInputDisabled ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => composerFileInputRef.current?.click()}
                    disabled={composerLocked}
                    data-testid="chat-attach-button"
                    title={t("questionnaire.attachFiles")}
                    aria-label={t("questionnaire.attachFiles")}
                    className="absolute bottom-[10px] right-[52px] flex size-[32px] items-center justify-center rounded-[8px] text-twin-chat-fg-muted transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <input
                    ref={composerFileInputRef}
                    type="file"
                    accept={ASSET_MEDIA_ACCEPT}
                    multiple
                    className="sr-only"
                    data-testid="chat-attach-input"
                    onChange={(event) => {
                      handleComposerMediaFiles(Array.from(event.target.files ?? []));
                      event.target.value = "";
                    }}
                  />
                  {surveyQuestionCount > 0 ? (
                    <button
                      onClick={() => setSidebarOpen(true)}
                      className="absolute bottom-[10px] right-[92px] flex h-[32px] items-center gap-1.5 rounded-lg bg-twin-chat-bubble/20 px-3 text-xs font-medium text-twin-chat-fg/70 hover:bg-twin-chat-bubble/30 lg:hidden"
                    >
                      <span>{surveyQuestionCount}Q</span>
                      <div className="h-2 w-2 rounded-full bg-white/40" />
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="hidden min-h-0 lg:flex lg:w-2/3">
          <QuestionnaireBuilderPanel
            draftId={draftId}
            hasUserSentMessage={hasUserSentMessage}
            language={language}
            onLanguageChange={
              onLanguageChange ? handleLanguageSelect : undefined
            }
            studyBrand={studyBrand}
            onStudyBrandSave={hasDraft ? saveStudyBrand : undefined}
            workspace={workspace}
            highlightValues={[]}
            busy={busyCause}
            turnStage={turnStage}
            turnStageTotal={turnStageTotal}
            stopRequested={stopRequested}
            onSkipLogicDetectingChange={setDetectingSkipLogic}
            onSaveEditsFirst={saveEditsFirst}
            isOpeningWorkspace={isOpeningWorkspace}
            coverageByQuestionId={coverageByQuestionId}
            issuesByQuestionId={indexedIssues.byQuestionId}
            unscopedIssues={indexedIssues.unscoped}
            issuesByAssetId={indexedIssues.byAssetId}
            advisoriesByQuestionId={indexedAdvisories.byQuestionId}
            unscopedAdvisories={indexedAdvisories.unscoped}
            studyGoals={studyGoals}
            onSaveStudyGoals={saveObjectiveAfterEdits}
            onObjectiveEditorChange={onObjectiveEditorChange}
            isSavingStudyGoals={isSavingStudyGoals}
            onSave={saveFromPanel}
          />
        </div>
      </div>

      <CreationFlowStickyActionBar
        className="px-0"
        start={
          onEditAudience ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => void editAudience()}
              // Its audience write commits at the revision it read, so it
              // waits for the turn or Save in flight.
              disabled={chatInputDisabled}
              data-testid="chat-edit-audience-button"
              className="h-8 rounded-md pl-0 pr-3 text-[13px] font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              {t("questionnaire.editAudience")}
            </Button>
          ) : undefined
        }
        center={
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Wrapper span keeps the tooltip working while the button is
                    disabled (disabled buttons don't emit pointer events). */}
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="cta"
                    data-testid="questionnaire-builder-continue-button"
                    aria-describedby={
                      reviewEligible ? undefined : REVIEW_BLOCKED_REASON_ID
                    }
                    onClick={handleAdvance}
                    disabled={
                      chatInputDisabled || !reviewEligible || isAdvancing
                    }
                    className="h-10 rounded-md px-4 text-[16px] font-medium tracking-[-0.02em]"
                  >
                    {isAdvancing ? (
                      <>
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        {t("questionnaire.reviewing")}
                      </>
                    ) : (
                      t("questionnaire.reviewStudy")
                    )}
                  </Button>
                  {/* The tooltip's reason, for a screen reader and the
                      keyboard, which never reach a tooltip on a disabled
                      button (B22). */}
                  {!reviewEligible ? (
                    <span id={REVIEW_BLOCKED_REASON_ID} className="sr-only">
                      {reviewBlockedReason}
                    </span>
                  ) : null}
                </span>
              </TooltipTrigger>
              {!reviewEligible ? (
                <TooltipContent side="top">
                  <span
                    className="text-[11px]"
                    data-testid="questionnaire-review-blocked-reason"
                  >
                    {reviewBlockedReason}
                  </span>
                </TooltipContent>
              ) : null}
            </Tooltip>
          </TooltipProvider>
        }
      />

      <Dialog
        open={pendingLanguage !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingLanguage(null);
          }
        }}
      >
        <DialogContent
          className="max-w-sm"
          data-testid="language-translate-confirm"
        >
          {/* What a translation changes, exactly (python-api
              `translation.py`): the respondents' text, in the interface's
              language like the rest of the builder. The tested material is
              the user's and stays as written. */}
          <DialogHeader>
            <DialogTitle>{t("questionnaire.translateTitle")}</DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                {t("questionnaire.translateBody", {
                  language: languageName(dialogLanguage ?? ""),
                })}
              </span>
              <span className="block">
                {t("questionnaire.translateMaterial", {
                  language: languageName(dialogLanguage ?? ""),
                })}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingLanguage(null)}
              data-testid="language-translate-cancel"
            >
              {t("questionnaire.translateKeep", {
                language: languageName(language ?? "en"),
              })}
            </Button>
            <Button
              type="button"
              variant="cta"
              onClick={confirmLanguageTranslate}
              data-testid="language-translate-confirm-action"
            >
              {t("questionnaire.translateConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={saveContinueOpen}
        onOpenChange={(open) => {
          if (!open) settleSaveContinue(false);
        }}
      >
        <DialogContent className="max-w-sm" data-testid="save-and-continue-confirm">
          <DialogHeader>
            <DialogTitle>{t("questionnaire.saveContinueTitle")}</DialogTitle>
            <DialogDescription>
              {t("questionnaire.saveContinueBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => settleSaveContinue(false)}
              data-testid="save-and-continue-cancel"
            >
              {t("questionnaire.keepEditing")}
            </Button>
            <Button
              type="button"
              variant="cta"
              onClick={() => settleSaveContinue(true)}
              data-testid="save-and-continue-action"
            >
              {t("questionnaire.saveAndContinue")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileDrawer open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <QuestionnaireBuilderPanel
          draftId={draftId}
          hasUserSentMessage={hasUserSentMessage}
          language={language}
          onLanguageChange={onLanguageChange ? handleLanguageSelect : undefined}
          workspace={workspace}
          highlightValues={[]}
          busy={busyCause}
          turnStage={turnStage}
          turnStageTotal={turnStageTotal}
          stopRequested={stopRequested}
          onSkipLogicDetectingChange={setDetectingSkipLogic}
          onSaveEditsFirst={saveEditsFirst}
          isOpeningWorkspace={isOpeningWorkspace}
          coverageByQuestionId={coverageByQuestionId}
          issuesByQuestionId={indexedIssues.byQuestionId}
          unscopedIssues={indexedIssues.unscoped}
          issuesByAssetId={indexedIssues.byAssetId}
          advisoriesByQuestionId={indexedAdvisories.byQuestionId}
          unscopedAdvisories={indexedAdvisories.unscoped}
          studyGoals={studyGoals}
          onSaveStudyGoals={saveObjectiveAfterEdits}
          onObjectiveEditorChange={onObjectiveEditorChange}
          isSavingStudyGoals={isSavingStudyGoals}
          onSave={saveFromPanel}
        />
      </MobileDrawer>
    </div>
  );
}
